import { Fragment, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { alpha } from "@mui/material/styles";
import { useLocation } from "react-router-dom";
import {
  Avatar,
  Box,
  Button,
  Chip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  IconButton,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { LocalShippingOutlined as LocalShippingOutlinedIcon, CheckCircleOutlineOutlined as CheckCircleOutlineOutlinedIcon, WarningAmberOutlined as WarningAmberOutlinedIcon, DirectionsCarFilledOutlined as DirectionsCarFilledOutlinedIcon, BadgeOutlined as BadgeOutlinedIcon, AccessTimeOutlined as AccessTimeOutlinedIcon, PlaceOutlined as PlaceOutlinedIcon, ArrowOutwardOutlined as ArrowOutwardOutlinedIcon, RouteOutlined as RouteOutlinedIcon, RefreshOutlined as RefreshOutlinedIcon, BuildOutlined as BuildOutlinedIcon, ExpandMore as ExpandMoreIcon, CloseOutlined as CloseOutlinedIcon } from "../../../components/icons";
import Loader from "../../../components/Loader";
import {
  completeFuneralFleetAllocation,
  fetchAvailableDrivers,
  fetchAvailableVehicles,
  reassignFleetAllocation,
  getFleetBodyTransfers,
  getFleetPortal,
  getFleetReportSummary,
  getFuneralPlanningTaskQueue,
} from "../../../services/dashboard";
import { useToast } from "../../../hooks/useToast";
import {
  OperationsConfirmDialog,
  OperationsEmptyState,
  OperationsPageShell,
  OperationsSectionHeader,
  formatOperationsStatus,
  ifasColors,
} from "../ifas/components/OperationsUI";
import {
  StandardCompactStatusTile,
  StandardCountBadge,
  StandardDetailCard,
  StandardLabelledTabs,
  StandardMetricCard,
  StandardPager,
  standardGhostBtnSx,
  standardManageLinkSx,
  standardRowActionBtnSx,
  standardTableHeadCellSx,
  standardTableRowSx,
  StatusDotChip,
  STANDARD_TONES,
  operationsStatusTone,
  type StandardLabelledTab,
  type Tone,
} from "../ifas/components/StandardUI";
import FleetGlobalStrip from "./FleetGlobalStrip";
import { canDriverUseVehicle } from "../../../utils/fleetCompatibility";

export type FleetTab =
  | "overview"
  | "requests"
  | "vehicles"
  | "drivers"
  | "assignments"
  | "transfers"
  | "alerts";

/**
 * Options object for driver-screen navigation. Carries BOTH ded9fe4's slot
 * assignment context (allocationRole/allocationSequence) AND the Fleet IA's
 * per-driver deep-link (focusDriverNumber). Single options arg — never extend
 * positional args (Sam mandate).
 */
export type DriversNavArgs = {
  caseNumber?: string;
  assignmentPurpose?: string;
  requestReference?: string;
  allocationRole?: string;
  allocationSequence?: number;
  focusDriverNumber?: string;
};

/**
 * Canonical allocation-completeness ("awaiting") predicate — backed for BOTH
 * funeral (FLEET) and warehouse-transport requests. A request is still
 * awaiting while it has an unfilled vehicle slot, even a 2-of-3 one.
 */
const isRequestAwaitingAllocation = (request: any) =>
  Number(request?.allocatedVehicleCount || 0) < Number(request?.requiredVehicleCount || 1);

/**
 * Per-destination subline, copied verbatim from each mock's `.sub` line:
 * `fleet-portal-{overview,requests,vehicles,drivers,assignments,transfers,alerts}.html`.
 */
const FLEET_TAB_DESCRIPTIONS: Record<FleetTab, string> = {
  overview:
    "A summary of today's fleet activity, with each item linking through to its full operational surface. Nothing here is a working table — drill in for that.",
  requests:
    "Funeral-service and warehouse-transport jobs awaiting a driver and vehicle. Assign hands the case straight to the Drivers screen, pre-scoped.",
  vehicles:
    "Live vehicle status and where each vehicle is right now. A monitoring surface — manage vehicle records in the Vehicles register.",
  drivers:
    "Live driver status, shift coverage and alerts. A monitoring surface — manage driver records in the Drivers register.",
  assignments:
    "Every in-flight allocation across the fleet, filtered by lifecycle. Use a row's Open action for its full detail — no separate Operations Focus panel.",
  transfers:
    "Inter-branch body transfers, newest first, across all ten lifecycle states. A read-only rollup — act on the Drivers / Vehicles screens.",
  alerts:
    "Only genuine, computed alerts appear here — each links straight to its driver or vehicle. Nothing invented, nothing double-counted.",
};

const FLEET_TAB_VALUES: FleetTab[] = [
  "overview",
  "requests",
  "vehicles",
  "drivers",
  "assignments",
  "transfers",
  "alerts",
];

type EventTabKey = "all" | "active" | "upcoming" | "completed" | "delayed";
type FleetRequestTypeFilter = "ALL" | "FLEET" | "WAREHOUSE_TRANSPORT";
type FleetRequestStatusFilter = "ALL" | "REQUESTED" | "IN_PROGRESS";

const FLEET_REQUEST_PAGE_SIZE = 6;
const BODY_TRANSFER_PAGE_SIZE = 8;
const EVENT_PAGE_SIZE = 8;
type EventStatusFilter = "ALL" | "ON_TIME" | "DELAYED" | "COMPLETED" | "CANCELLED";

/**
 * Body-transfer status vocabulary — all 10 lifecycle states toned per the
 * approved mock (`fleet-portal-transfers.html`): amber Requested / Pending
 * Approval / Delayed, blue Dispatched / In Transit, green Approved / Arrived
 * / Completed, red Rejected / Cancelled. Kept local to Fleet since the shared
 * `standardStatusTone` keyword-matcher does not resolve IN_TRANSIT to blue.
 */
const BODY_TRANSFER_STATUS_TONES: Record<string, Tone> = {
  REQUESTED: "amber",
  PENDING_APPROVAL: "amber",
  APPROVED: "green",
  DISPATCHED: "blue",
  IN_TRANSIT: "blue",
  ARRIVED: "green",
  COMPLETED: "green",
  REJECTED: "red",
  CANCELLED: "red",
  DELAYED: "amber",
};

const bodyTransferStatusTone = (value?: string): Tone => {
  const key = String(value || "").trim().toUpperCase().replace(/[\s-]+/g, "_");
  return BODY_TRANSFER_STATUS_TONES[key] ?? "grey";
};

/** FLEET-BUG-04: a Rejected/Cancelled/Delayed transfer never frees its vehicle — tracked, not fixed here. */
const BODY_TRANSFER_RELEASE_GAP_STATUSES = new Set(["REJECTED", "CANCELLED", "DELAYED"]);

/**
 * The ONE Fleet card token — matches every approved mock's `.card` exactly:
 * `background:var(--surface);border:1px solid var(--border);border-radius:16px;
 * box-shadow:var(--shadow)` where `--shadow:0 1px 2px rgba(16,24,40,.04),
 * 0 10px 26px rgba(20,24,31,.06)`. Every Fleet panel (Overview, Vehicles,
 * Drivers, Assignments, Transfers, Requests, Alerts) renders through this one
 * token instead of the three different shadows/radii that existed before
 * (`commandPanelSx`, `standardListCardSx`, `operationsTablePaperSx`).
 */
const fleetCardSx = {
  bgcolor: ifasColors.surface,
  border: `1px solid ${ifasColors.border}`,
  borderRadius: "16px",
  boxShadow: "0 1px 2px rgba(16,24,40,.04), 0 10px 26px rgba(20,24,31,.06)",
};

/** `fleetCardSx` plus the panel-level padding the Overview/Vehicles/Drivers/
 * Alerts panels need (Transfers/Requests/Assignments build their own header
 * padding instead, so they use `fleetCardSx` bare). */
const commandPanelSx = {
  ...fleetCardSx,
  p: { xs: 1.5, md: 1.9 },
};

const subtleTileSx = {
  borderRadius: 2,
  border: `1px solid ${ifasColors.border}`,
  bgcolor: ifasColors.surface,
};

// Fleet Requests tab — the mock's `.banner` (Requests Queue title/description + search + filters).
const requestsBannerSx = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 1.75,
  flexWrap: "wrap" as const,
  p: "11px 16px",
  borderRadius: "14px",
  border: `1px solid ${ifasColors.border}`,
  background: `linear-gradient(135deg, ${ifasColors.softOrange}, ${ifasColors.surface} 46%)`,
  boxShadow: "0 4px 14px rgba(20,24,31,.05)",
};

const requestsBannerIconSx = {
  width: 36,
  height: 36,
  borderRadius: "10px",
  bgcolor: ifasColors.softOrange,
  color: ifasColors.orange,
  display: "grid",
  placeItems: "center",
  flexShrink: 0,
};

const EVENT_TABS: Array<{
  key: EventTabKey;
  label: string;
  tone: Tone;
}> = [
  { key: "all", label: "All assignments", tone: "blue" },
  { key: "active", label: "Active", tone: "blue" },
  { key: "upcoming", label: "Upcoming", tone: "amber" },
  { key: "completed", label: "Completed", tone: "green" },
  { key: "delayed", label: "Delayed / At Risk", tone: "red" },
];

const formatDateTime = (value?: string | null) => {
  if (!value) return "Not scheduled";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
};

const formatSchedule = (date?: string | null, time?: string | null) => {
  if (!date && !time) return "Not scheduled";
  if (!time) return date || "Not scheduled";
  return `${date || ""} ${time}`.trim();
};

const resolveKey = (...values: Array<any>) => values.find((value) => value !== undefined && value !== null && value !== "") || "";

const getInitials = (value?: string | null) => {
  const text = String(value || "").trim();
  if (!text) return "NA";
  const parts = text.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ""}${parts[1][0] || ""}`.toUpperCase();
};

/**
 * Assignments row's "Event" chip — the timeliness dimension (On Time /
 * Delayed·At Risk / Cancelled / Completed), tone-mapped onto the shared
 * `STANDARD_TONES` vocabulary rather than a bespoke colour set.
 */
const eventTimelinessTone = (value?: string | null): { label: string; tone: Tone } => {
  const normalized = String(value || "").toUpperCase();

  if (normalized.includes("ON_TIME") || normalized.includes("ON TIME") || normalized.includes("HEALTHY")) {
    return { label: "On Time", tone: "green" };
  }

  if (normalized.includes("DELAY") || normalized.includes("RISK") || normalized.includes("WARNING")) {
    return { label: "Delayed / At Risk", tone: "amber" };
  }

  if (normalized.includes("CANCEL")) {
    return { label: "Cancelled", tone: "red" };
  }

  if (normalized.includes("COMPLET")) {
    return { label: "Completed", tone: "green" };
  }

  return { label: formatOperationsStatus(value || "Monitored"), tone: "grey" };
};

const getEventRows = (summary: any, tab: EventTabKey) => {
  switch (tab) {
    case "all": {
      const rows = [
        ...(summary?.delayedOrAtRiskEvents || []),
        ...(summary?.activeEvents || []),
        ...(summary?.upcomingEvents || []),
        ...(summary?.completedEventsToday || []),
      ];
      return Array.from(new Map(rows.map((item: any) => [item.allocationId, item])).values());
    }
    case "active":
      return summary?.activeEvents || [];
    case "upcoming":
      return summary?.upcomingEvents || [];
    case "completed":
      return summary?.completedEventsToday || [];
    case "delayed":
      return summary?.delayedOrAtRiskEvents || [];
    default:
      return [];
  }
};


/**
 * Overview's "Urgent" drill-through chip — the approved mock's `.u-chip`:
 * a dashed, tone-coloured pill that jumps straight to the destination tab
 * that owns the underlying data.
 */
function UrgentChip({
  tone,
  label,
  onClick,
}: {
  tone: Tone;
  label: string;
  onClick: () => void;
}) {
  const t = STANDARD_TONES[tone];
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      sx={{
        fontFamily: "inherit",
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
        fontSize: 12,
        fontWeight: 800,
        color: t.color,
        bgcolor: t.bg,
        border: `1px dashed ${alpha(t.color, 0.55)}`,
        borderRadius: "999px",
        px: 1.35,
        py: 0.7,
        cursor: "pointer",
      }}
    >
      <Box component="span" sx={{ width: 6, height: 6, borderRadius: "999px", bgcolor: t.color, flexShrink: 0 }} />
      {label}
    </Box>
  );
}

/** Alert icon per real `alertType` — matches the approved Alerts mock's `.arow-ic` glyphs. */
const ALERT_TYPE_ICON: Record<string, ReactNode> = {
  LICENSE_EXPIRY: <BadgeOutlinedIcon />,
  OVERTIME_WARNING: <AccessTimeOutlinedIcon />,
  TRIP_DELAY: <WarningAmberOutlinedIcon />,
  VEHICLE_MAINTENANCE: <BuildOutlinedIcon />,
};

/** Severity -> tone, exactly the mock's `.hi` (red/High) / `.med` (amber/Medium). */
const alertSeverityTone = (severity?: string): Tone =>
  String(severity || "").toUpperCase() === "HIGH" ? "red" : "amber";

/**
 * Compact alert list row — the approved Alerts mock's `.arow`: icon, title +
 * inline detail + reference, a context link into the owning destination tab,
 * and a severity chip. Replaces the old tall alert card.
 */
function FleetAlertRow({ item, onOpen, linkLabel }: { item: any; onOpen: () => void; linkLabel: string }) {
  const tone = STANDARD_TONES[alertSeverityTone(item.severity)];
  const icon = ALERT_TYPE_ICON[item.alertType] || <WarningAmberOutlinedIcon />;
  return (
    <Box
      sx={{
        display: "flex",
        gap: 1.1,
        alignItems: "center",
        py: 1,
        px: 0.25,
        borderBottom: `1px solid ${ifasColors.rule}`,
        "&:last-of-type": { borderBottom: "none" },
      }}
    >
      <Box
        sx={{
          width: 26,
          height: 26,
          borderRadius: "7px",
          display: "grid",
          placeItems: "center",
          flexShrink: 0,
          bgcolor: tone.bg,
          color: tone.color,
          "& svg": { fontSize: 14 },
        }}
      >
        {icon}
      </Box>
      <Box flex={1} minWidth={0} display="flex" alignItems="baseline" gap={0.9} flexWrap="wrap">
        <Typography fontSize={12.5} fontWeight={800} color={ifasColors.text} whiteSpace="nowrap">
          {item.title}
        </Typography>
        <Typography fontSize={12} fontWeight={600} color={ifasColors.muted} lineHeight={1.3}>
          {item.description}
        </Typography>
        {item.referenceNumber && (
          <Typography fontSize={11} fontWeight={800} color={ifasColors.deepOrange} flexShrink={0}>
            {item.referenceNumber}
          </Typography>
        )}
      </Box>
      <Button
        size="small"
        onClick={onOpen}
        sx={{ ...standardManageLinkSx, flexShrink: 0, minHeight: 26, fontSize: "11px", py: 0.3, px: 1 }}
      >
        {linkLabel} →
      </Button>
      <Chip
        size="small"
        label={
          <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.6 }}>
            <Box component="span" sx={{ width: 6, height: 6, borderRadius: "999px", bgcolor: "currentColor", flexShrink: 0 }} />
            {alertSeverityTone(item.severity) === "red" ? "High" : "Medium"}
          </Box>
        }
        sx={{
          color: tone.color,
          bgcolor: tone.bg,
          fontWeight: 800,
          fontSize: 11,
          borderRadius: "8px",
          height: 22,
          flexShrink: 0,
        }}
      />
    </Box>
  );
}

function FleetAlertPanel({
  title,
  items,
  linkLabel,
  onOpen,
}: {
  title: string;
  items: any[];
  linkLabel: string;
  onOpen: () => void;
}) {
  return (
    <Paper sx={commandPanelSx} elevation={0}>
      <Box display="flex" alignItems="center" justifyContent="space-between" gap={1} mb={0.5}>
        <OperationsSectionHeader title={title} />
        <StandardCountBadge count={items.length} />
      </Box>
      {!items.length ? (
        <Box
          sx={{
            ...subtleTileSx,
            py: 4,
            px: 2,
            textAlign: "center",
            bgcolor: ifasColors.surface,
          }}
        >
          <Typography fontWeight={900} color={ifasColors.text}>
            All quiet here
          </Typography>
          <Typography fontSize={13} color={ifasColors.muted} mt={0.5}>
            No alerts are demanding attention right now.
          </Typography>
        </Box>
      ) : (
        items.map((item, index) => (
          <FleetAlertRow
            key={`${item.alertType || "alert"}-${item.referenceNumber || index}`}
            item={item}
            linkLabel={linkLabel}
            onOpen={onOpen}
          />
        ))
      )}
    </Paper>
  );
}

export default function FleetPortal({
  onDriversClicked,
  onVehiclesClicked,
}: {
  onDriversClicked?: (args?: DriversNavArgs) => void;
  onVehiclesClicked?: () => void;
}) {
  const location = useLocation();
  const notificationFleetReference = String(location.state?.fleetRequestReference || "");
  const notificationCaseNumber = String(location.state?.caseNumber || "");
  const [isInitialLoading, setIsInitialLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [fleetSummary, setFleetSummary] = useState<any>(null);
  const [selectedFleetTab, setSelectedFleetTab] = useState<FleetTab>("overview");
  const [selectedEventTab, setSelectedEventTab] = useState<EventTabKey>("all");
  const [selectedEventKey, setSelectedEventKey] = useState("");
  const [eventSearch, setEventSearch] = useState("");
  const [eventVehicleTypeFilter, setEventVehicleTypeFilter] = useState("ALL");
  const [eventStatusFilter, setEventStatusFilter] = useState<EventStatusFilter>("ALL");
  const [eventPage, setEventPage] = useState(1);
  const [fleetReport, setFleetReport] = useState<any>(null);
  const [bodyTransfers, setBodyTransfers] = useState<any[]>([]);
  const [bodyTransferPage, setBodyTransferPage] = useState(0);
  const [bodyTransferTotal, setBodyTransferTotal] = useState(0);
  const bodyTransferPageRef = useRef(0);
  const [funeralFleetRequests, setFuneralFleetRequests] = useState<any[]>([]);
  const [expandedFleetRequests, setExpandedFleetRequests] = useState<Set<string>>(new Set());
  const [fleetRequestTypeFilter, setFleetRequestTypeFilter] = useState<FleetRequestTypeFilter>("ALL");
  const [fleetRequestStatusFilter, setFleetRequestStatusFilter] = useState<FleetRequestStatusFilter>("ALL");
  const [fleetRequestSearch, setFleetRequestSearch] = useState("");
  const [fleetRequestPage, setFleetRequestPage] = useState(1);
  const [completingAllocationId, setCompletingAllocationId] = useState("");
  const [reassignment, setReassignment] = useState<any>(null);
  const [replacementDriverId, setReplacementDriverId] = useState("");
  const [replacementVehicleId, setReplacementVehicleId] = useState("");
  const [reassignmentReason, setReassignmentReason] = useState("");
  const [availableDrivers, setAvailableDrivers] = useState<any[]>([]);
  const [availableVehicles, setAvailableVehicles] = useState<any[]>([]);
  const [savingReassignment, setSavingReassignment] = useState(false);
  const replacementDriver = replacementDriverId === reassignment?.driverId
    ? { licenseCode: reassignment?.driverLicenseCode, hasPdp: reassignment?.driverHasPdp }
    : availableDrivers.find((driver: any) => driver.driverID === replacementDriverId);
  const currentReassignmentVehicle = {
    requiredLicenseCode: reassignment?.vehicleRequiredLicenseCode,
    requiresPdp: reassignment?.vehicleRequiresPdp,
  };
  const [pendingCompleteTrip, setPendingCompleteTrip] = useState<{
    allocationId: string;
    driverName: string;
    vehicleLabel: string;
    requestReference: string;
  } | null>(null);
  const { showSuccessToast, showErrorToast } = useToast();

  const openReassignment = async (item: any) => {
    setReassignment(item);
    setReplacementDriverId(item.driverId || "");
    setReplacementVehicleId(item.vehicleId || "");
    setReassignmentReason("");
    const [driversResponse, vehiclesResponse]: any[] = await Promise.all([
      fetchAvailableDrivers(0, 1000),
      fetchAvailableVehicles(0, 1000),
    ]);
    setAvailableDrivers(driversResponse?.data?.drivers || driversResponse?.data?.driver || []);
    setAvailableVehicles(vehiclesResponse?.data?.vehicles || vehiclesResponse?.data?.vehicle || []);
  };

  const saveReassignment = async () => {
    if (!reassignment?.allocationId || !replacementDriverId || !replacementVehicleId) {
      showErrorToast("Select a driver and vehicle.");
      return;
    }
    if (replacementDriverId === reassignment.driverId && replacementVehicleId === reassignment.vehicleId) {
      showErrorToast("Choose a different driver or vehicle.");
      return;
    }
    setSavingReassignment(true);
    const response: any = await reassignFleetAllocation(reassignment.allocationId, {
      driverId: replacementDriverId,
      vehicleId: replacementVehicleId,
      reason: reassignmentReason.trim() || "Resource replacement",
      assignedBy: "Fleet Coordinator",
    });
    setSavingReassignment(false);
    if (response?.status === 200) {
      setReassignment(null);
      showSuccessToast("Duty reassigned. Driver assignments have been updated.");
      await loadFleetPortal();
    } else {
      showErrorToast(response?.data?.message || "Could not reassign this duty.");
    }
  };

  const completeFleetTrip = async (allocationId: string) => {
    if (!allocationId || completingAllocationId) return;
    setCompletingAllocationId(allocationId);
    const response: any = await completeFuneralFleetAllocation(allocationId, {
      completedBy: "Fleet Coordinator",
    });
    setCompletingAllocationId("");
    setPendingCompleteTrip(null);
    if ([200, 201].includes(Number(response?.status))) {
      showSuccessToast("Fleet trip completed. Driver and vehicle are available again.");
      await loadFleetPortal();
    } else {
      showErrorToast(response?.data?.message || "Fleet trip could not be completed.");
    }
  };

  const toggleFleetRequestDetails = (requestKey: string) => {
    setExpandedFleetRequests((current) => {
      const next = new Set(current);
      if (next.has(requestKey)) next.delete(requestKey);
      else next.add(requestKey);
      return next;
    });
  };

  const loadFleetPortal = async () => {
    const isFirstLoad = !fleetSummary;
    try {
      if (isFirstLoad) {
        setIsInitialLoading(true);
      } else {
        setIsRefreshing(true);
      }
      const response = await getFleetPortal();
      if (response?.status === 200) {
        setFleetSummary(response.data);
      }
      const [reportResponse, transferResponse, requestedFuneralResponse, activeFuneralResponse] = await Promise.all([
        getFleetReportSummary(),
        getFleetBodyTransfers(bodyTransferPageRef.current, BODY_TRANSFER_PAGE_SIZE),
        getFuneralPlanningTaskQueue({ assignedTo: "FLEET_CENTRE_COORDINATOR", taskStatus: "REQUESTED", page: 0, size: 50 }),
        getFuneralPlanningTaskQueue({ assignedTo: "FLEET_CENTRE_COORDINATOR", taskStatus: "IN_PROGRESS", page: 0, size: 50 }),
      ]);
      if (reportResponse?.status === 200) {
        setFleetReport(reportResponse.data);
      }
      if (transferResponse?.status === 200) {
        const transferPage = transferResponse.data || {};
        const transferRows = transferPage.content || transferPage.bodyTransfers || [];
        setBodyTransfers(transferRows);
        setBodyTransferTotal(
          typeof transferPage.totalElements === "number" ? transferPage.totalElements : transferRows.length,
        );
      }
      if (requestedFuneralResponse?.status === 200 || activeFuneralResponse?.status === 200) {
        const combinedRequests = [
          ...(requestedFuneralResponse?.data?.tasks || []),
          ...(activeFuneralResponse?.data?.tasks || []),
        ];
        setFuneralFleetRequests(Array.from(
          new Map(combinedRequests
            .filter((task: any) => ["FLEET", "WAREHOUSE_TRANSPORT"].includes(task.taskType))
            .map((task: any) => [task.id || `${task.caseNumber}-${task.referenceNumber}`, task])).values(),
        ));
      }
      setLoadError(null);
    } catch (error) {
      setLoadError("We couldn't load the fleet portal. Please try again.");
    } finally {
      setIsInitialLoading(false);
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    void loadFleetPortal();
    const refreshTimer = window.setInterval(() => {
      void loadFleetPortal();
    }, 30_000);
    return () => window.clearInterval(refreshTimer);
  }, []);

  // Genuine server-side pagination: `bodyTransferPageRef` keeps the 30s shared
  // refresh (above, mount-once) fetching whichever page is current, and this
  // effect issues a fresh `getFleetBodyTransfers(page, size)` request the
  // moment the user changes page — never a client-side slice of a 5-row fetch.
  useEffect(() => {
    bodyTransferPageRef.current = bodyTransferPage;
    if (bodyTransferPage === 0) return; // page 0 is already covered by the load-on-mount effect above
    let cancelled = false;
    (async () => {
      const response = await getFleetBodyTransfers(bodyTransferPage, BODY_TRANSFER_PAGE_SIZE);
      if (cancelled) return;
      if (response?.status === 200) {
        const transferPage = response.data || {};
        const transferRows = transferPage.content || transferPage.bodyTransfers || [];
        setBodyTransfers(transferRows);
        setBodyTransferTotal(
          typeof transferPage.totalElements === "number" ? transferPage.totalElements : transferRows.length,
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [bodyTransferPage]);

  useEffect(() => {
    const requestedTab = location.state?.fleetTab;
    if (typeof requestedTab === "string" && FLEET_TAB_VALUES.includes(requestedTab as FleetTab)) {
      setSelectedFleetTab(requestedTab as FleetTab);
    } else if (location.state?.fleetRequestReference) {
      setSelectedFleetTab("requests");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const liveOperations = fleetSummary?.liveOperationsOverview || {};
  const vehiclePanel = fleetSummary?.vehicleStatusPanel || {};
  const driverPanel = fleetSummary?.driverStatusPanel || {};
  const vehicleAssignments = fleetSummary?.vehicleAssignments || [];
  const driverAssignments = fleetSummary?.driverAssignments || [];
  const eventRows = getEventRows(fleetSummary, selectedEventTab);

  // Vehicle-type filter options are derived from the real event payload
  // (union across all four lifecycle buckets), never hardcoded — the
  // vocabulary is whatever the backend is actually reporting today.
  const eventVehicleTypeOptions = useMemo(() => {
    const buckets = [
      fleetSummary?.activeEvents,
      fleetSummary?.upcomingEvents,
      fleetSummary?.completedEventsToday,
      fleetSummary?.delayedOrAtRiskEvents,
    ];
    const values = new Set<string>();
    buckets.forEach((bucket) => (bucket || []).forEach((item: any) => {
      if (item?.vehicleType) values.add(String(item.vehicleType));
    }));
    return Array.from(values).sort();
  }, [fleetSummary]);

  // Route-Watch echo (field audit): the assigned vehicle's live `currentLocation`
  // is the same `vehicleAssignments` datum the Vehicles tab's Route Watch uses —
  // looked up by vehicle number rather than duplicating a second location model.
  const vehicleByNumber = useMemo(() => {
    const map = new Map<string, any>();
    (fleetSummary?.vehicleAssignments || []).forEach((vehicle: any) => {
      if (vehicle?.vehicleNumber) map.set(String(vehicle.vehicleNumber), vehicle);
    });
    return map;
  }, [fleetSummary]);

  const filteredEventRows = useMemo(() => {
    const search = eventSearch.trim().toLowerCase();
    return eventRows.filter((item: any) => {
      if (eventVehicleTypeFilter !== "ALL" && String(item.vehicleType || "") !== eventVehicleTypeFilter) return false;
      if (eventStatusFilter !== "ALL") {
        const tone = eventTimelinessTone(item.statusIndicator || item.operationalStatus);
        const bucket = tone.label === "On Time" ? "ON_TIME"
          : tone.label === "Delayed / At Risk" ? "DELAYED"
          : tone.label === "Cancelled" ? "CANCELLED"
          : tone.label === "Completed" ? "COMPLETED"
          : "";
        if (bucket !== eventStatusFilter) return false;
      }
      if (!search) return true;
      return [
        item.vehicleNumber,
        item.driverName,
        item.caseNumber,
        item.bodyRemovalNumber,
        item.allocationNumber,
        item.pickupLocation,
        item.destinationLocation,
      ].some((value) => String(value || "").toLowerCase().includes(search));
    });
  }, [eventRows, eventSearch, eventStatusFilter, eventVehicleTypeFilter]);

  const eventPageCount = Math.max(1, Math.ceil(filteredEventRows.length / EVENT_PAGE_SIZE));
  const visibleEventRows = useMemo(() => {
    const start = (eventPage - 1) * EVENT_PAGE_SIZE;
    return filteredEventRows.slice(start, start + EVENT_PAGE_SIZE);
  }, [filteredEventRows, eventPage]);

  useEffect(() => {
    setEventPage(1);
  }, [selectedEventTab, eventSearch, eventStatusFilter, eventVehicleTypeFilter]);

  useEffect(() => {
    if (eventPage > eventPageCount) setEventPage(eventPageCount);
  }, [eventPage, eventPageCount]);

  const orderedFuneralFleetRequests = useMemo(() => [...funeralFleetRequests].sort((left: any, right: any) => {
    const leftSelected = String(left.referenceNumber || "") === notificationFleetReference
      || String(left.caseNumber || "") === notificationCaseNumber;
    const rightSelected = String(right.referenceNumber || "") === notificationFleetReference
      || String(right.caseNumber || "") === notificationCaseNumber;
    return Number(rightSelected) - Number(leftSelected);
  }), [funeralFleetRequests, notificationCaseNumber, notificationFleetReference]);
  const filteredFleetRequests = useMemo(() => {
    const search = fleetRequestSearch.trim().toLowerCase();
    return orderedFuneralFleetRequests.filter((request: any) => {
      if (fleetRequestTypeFilter !== "ALL" && request.taskType !== fleetRequestTypeFilter) return false;
      if (fleetRequestStatusFilter !== "ALL" && request.taskStatus !== fleetRequestStatusFilter) return false;
      if (!search) return true;
      return [
        request.referenceNumber,
        request.planNumber,
        request.caseNumber,
        request.funeralFileNumber,
        request.pickupLocation,
        request.bodyLocationName,
        request.funeralVenue,
        request.cemeteryDetails,
      ].some((value) => String(value || "").toLowerCase().includes(search));
    });
  }, [fleetRequestSearch, fleetRequestStatusFilter, fleetRequestTypeFilter, orderedFuneralFleetRequests]);
  const fleetRequestPageCount = Math.max(1, Math.ceil(filteredFleetRequests.length / FLEET_REQUEST_PAGE_SIZE));
  const visibleFleetRequests = useMemo(() => {
    const start = (fleetRequestPage - 1) * FLEET_REQUEST_PAGE_SIZE;
    return filteredFleetRequests.slice(start, start + FLEET_REQUEST_PAGE_SIZE);
  }, [filteredFleetRequests, fleetRequestPage]);

  useEffect(() => {
    setFleetRequestPage(1);
  }, [fleetRequestSearch, fleetRequestStatusFilter, fleetRequestTypeFilter]);

  useEffect(() => {
    if (fleetRequestPage > fleetRequestPageCount) setFleetRequestPage(fleetRequestPageCount);
  }, [fleetRequestPage, fleetRequestPageCount]);

  // Dissolved Operations Focus used to auto-select (and auto-open) the first
  // event on load. The Assignments mock opens detail only via an explicit Open
  // action, so this effect only collapses a row that has scrolled out of the
  // current tab/filter/page — it never opens one on its own.
  useEffect(() => {
    if (!selectedEventKey) return;
    const stillExists = eventRows.some(
      (item: any) =>
        String(resolveKey(item.allocationId, item.allocationNumber, "")) === String(selectedEventKey),
    );
    if (!stillExists) {
      setSelectedEventKey("");
    }
  }, [eventRows, selectedEventKey]);


  // Overview KPI cards — mirrors the approved Overview mock's 4 `.kpi` tiles
  // exactly: Active=orange, Upcoming=blue, Completed=green, Delayed=amber,
  // with the mock's own caption copy. The mock draws no icon on these cards.
  const topMetrics: Array<{
    key: EventTabKey;
    label: string;
    value: number;
    caption: string;
    tone: Tone;
  }> = [
    {
      key: "active",
      label: "Active Events",
      value: liveOperations.activeEvents || 0,
      caption: "In progress right now",
      tone: "orange",
    },
    {
      key: "upcoming",
      label: "Upcoming",
      value: liveOperations.upcomingEvents || 0,
      caption: "Scheduled, not yet started",
      tone: "blue",
    },
    {
      key: "completed",
      label: "Completed Today",
      value: liveOperations.completedEventsToday || 0,
      caption: "Closed out since midnight",
      tone: "green",
    },
    {
      key: "delayed",
      label: "Delayed / At Risk",
      value: liveOperations.delayedOrAtRiskEvents || 0,
      caption: "→ Assignments",
      tone: "amber",
    },
  ];

  // 6 tiles, in the approved Vehicles mock's order — Available, Allocated,
  // Dispatched, In Service, Maintenance, Out of Service. `inServiceVehicles`
  // is now a real backend field (VehicleStatusPanel.inServiceVehicles).
  const vehicleMetrics: Array<{ label: string; value: number; tone: Tone }> = [
    { label: "Available", value: vehiclePanel.availableVehicles || 0, tone: "green" },
    { label: "Allocated", value: vehiclePanel.allocatedVehicles || 0, tone: "purple" },
    { label: "Dispatched", value: vehiclePanel.dispatchedVehicles || 0, tone: "blue" },
    { label: "In Service", value: vehiclePanel.inServiceVehicles || 0, tone: "blue" },
    { label: "Maintenance", value: vehiclePanel.underMaintenanceVehicles || 0, tone: "amber" },
    // P1b/D1 (Sam-approved): OUT_OF_SERVICE uses the shared IFAS red semantic tone everywhere in
    // Fleet — tile included — so one status reads as one colour. Shared token, not a literal red.
    { label: "Out of Service", value: vehiclePanel.outOfServiceVehicles || 0, tone: operationsStatusTone("OUT_OF_SERVICE") },
  ];

  // 5 status tiles + the Alerts tile, in the approved Drivers mock's order.
  // The Alerts tile is the only real, computed driver alert signal
  // (licence-expiry + overtime) — no other alert types are fabricated in.
  const driverMetrics: Array<{ label: string; value: number; tone: Tone; alert?: boolean }> = [
    { label: "Available", value: driverPanel.availableDrivers || 0, tone: "green" },
    { label: "Assigned", value: driverPanel.assignedDrivers || 0, tone: "purple" },
    { label: "Dispatched", value: driverPanel.dispatchedDrivers || 0, tone: "blue" },
    { label: "In Service", value: driverPanel.inServiceDrivers || 0, tone: "blue" },
    { label: "Unavailable", value: driverPanel.unavailableDrivers || 0, tone: "grey" },
    {
      label: "Alerts",
      value: (driverPanel.licenseExpiryAlerts || 0) + (driverPanel.overtimeWarnings || 0),
      tone: "amber",
      alert: true,
    },
  ];

  const eventTabCounts = {
    all: getEventRows(fleetSummary, "all").length,
    active: liveOperations.activeEvents || 0,
    upcoming: liveOperations.upcomingEvents || 0,
    completed: liveOperations.completedEventsToday || 0,
    delayed: liveOperations.delayedOrAtRiskEvents || 0,
  };


  const locationWatch = vehicleAssignments
    .filter((item: any) => item?.currentLocation || item?.currentBodyRemovalNumber || item?.currentCaseNumber)
    .slice(0, 4);

  const driverAlerts = fleetSummary?.driverAlerts || [];
  const vehicleAlerts = fleetSummary?.vehicleAlerts || [];
  const fleetAlertCount = driverAlerts.length + vehicleAlerts.length;

  // Overview "Urgent" drill-through counts — the same real figures the
  // Assignments/Alerts/Requests destinations compute themselves.
  const requestsAwaitingAllocationCount = funeralFleetRequests.filter(
    (request: any) => isRequestAwaitingAllocation(request),
  ).length;
  const licenceExpiryCount = driverPanel.licenseExpiryAlerts || 0;
  const delayedEventsCount = liveOperations.delayedOrAtRiskEvents || 0;

  const fleetTabs: Array<StandardLabelledTab<FleetTab>> = [
    { value: "overview", label: "Overview" },
    { value: "requests", label: "Fleet Requests" },
    { value: "vehicles", label: "Vehicles" },
    { value: "drivers", label: "Drivers" },
    { value: "assignments", label: "Assignments" },
    { value: "transfers", label: "Body Transfers" },
    { value: "alerts", label: "Alerts", count: fleetAlertCount },
  ];

  return (
    <OperationsPageShell
      eyebrow="Fleet Portal"
      title="Command Centre"
      description={FLEET_TAB_DESCRIPTIONS[selectedFleetTab]}
    >
      {isInitialLoading && <Loader isLoading={isInitialLoading} />}

      {!isInitialLoading && loadError && !fleetSummary ? (
        <Paper sx={{ ...commandPanelSx, py: 5 }} elevation={0}>
          <OperationsEmptyState
            title="Couldn't load the fleet portal"
            description={loadError}
          />
          <Box display="flex" justifyContent="center" mt={2}>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<RefreshOutlinedIcon />}
              onClick={() => void loadFleetPortal()}
            >
              Retry
            </Button>
          </Box>
        </Paper>
      ) : !isInitialLoading && !fleetSummary ? (
        <Paper sx={{ ...commandPanelSx, py: 5 }} elevation={0}>
          <OperationsEmptyState
            title="Fleet data is not available yet"
            description="Once the fleet portal endpoint returns operational data, this command centre will fill in automatically."
          />
        </Paper>
      ) : (
        <>
          <FleetGlobalStrip
            alertCount={fleetAlertCount}
            availableVehicles={vehiclePanel.availableVehicles || 0}
            availableDrivers={driverPanel.availableDrivers || 0}
            generatedAt={fleetSummary?.generatedAt}
            isRefreshing={isRefreshing}
            onRefresh={() => void loadFleetPortal()}
            onAlertsClick={() => setSelectedFleetTab("alerts")}
          />
          <StandardLabelledTabs
            tabs={fleetTabs}
            value={selectedFleetTab}
            onChange={setSelectedFleetTab}
          />

          {selectedFleetTab === "overview" && (
          <>
          {/* 1. 4 KPI metric cards — approved mock's `.kpis`. Delayed is the
              only clickable one: it drills into Assignments pre-filtered to
              the Delayed/At-Risk bucket. */}
          <Grid container spacing={1.5}>
            {topMetrics.map((metric) => {
              const isDelayed = metric.key === "delayed";
              return (
                <Grid key={metric.key} size={{ xs: 12, sm: 6, xl: 3 }}>
                  <StandardMetricCard
                    label={metric.label}
                    value={metric.value}
                    caption={metric.caption}
                    tone={metric.tone}
                    compact
                    onClick={
                      isDelayed
                        ? () => {
                            setSelectedEventTab("delayed");
                            setSelectedFleetTab("assignments");
                          }
                        : undefined
                    }
                  />
                </Grid>
              );
            })}
          </Grid>

          {/* 2 & 3. Fleet Reporting summary strip + Urgent drill-through chips */}
          <Box
            display="grid"
            gridTemplateColumns={{ xs: "1fr", md: "1.1fr 1fr" }}
            gap={1.5}
            mt={1.5}
          >
            <Paper sx={commandPanelSx} elevation={0}>
              <OperationsSectionHeader title="Fleet Reporting" meta="Register-wide totals" />
              <Box
                display="flex"
                alignItems="center"
                mt={0.75}
                sx={{
                  "& > div": {
                    flex: 1,
                    minWidth: 88,
                    textAlign: "center",
                    borderRight: `1px solid ${ifasColors.rule}`,
                  },
                  "& > div:last-of-type": { borderRight: "none" },
                }}
              >
                <Box>
                  <Typography fontSize={17} fontWeight={900} color={ifasColors.text} letterSpacing="-0.02em">
                    {fleetReport?.totalVehicles || vehiclePanel.totalVehicles || 0}
                  </Typography>
                  <Typography fontSize={10.5} fontWeight={700} color={ifasColors.muted} mt={0.2}>
                    Vehicles
                  </Typography>
                </Box>
                <Box>
                  <Typography fontSize={17} fontWeight={900} color={ifasColors.text} letterSpacing="-0.02em">
                    {fleetReport?.totalDrivers || driverPanel.totalDrivers || 0}
                  </Typography>
                  <Typography fontSize={10.5} fontWeight={700} color={ifasColors.muted} mt={0.2}>
                    Drivers
                  </Typography>
                </Box>
                <Box>
                  <Typography fontSize={17} fontWeight={900} color={ifasColors.text} letterSpacing="-0.02em">
                    {fleetReport?.totalMileageRecords || 0}
                  </Typography>
                  <Typography fontSize={10.5} fontWeight={700} color={ifasColors.muted} mt={0.2}>
                    Mileage
                  </Typography>
                </Box>
                <Box>
                  <Typography fontSize={17} fontWeight={900} color={ifasColors.text} letterSpacing="-0.02em">
                    {fleetReport?.totalBodyTransfers || 0}
                  </Typography>
                  <Typography fontSize={10.5} fontWeight={700} color={ifasColors.muted} mt={0.2}>
                    Body Transfers
                  </Typography>
                </Box>
              </Box>
            </Paper>

            <Paper sx={commandPanelSx} elevation={0}>
              <OperationsSectionHeader title="Urgent" meta="Drill-through cues" />
              <Box display="flex" flexWrap="wrap" gap={0.9} mt={0.75}>
                <UrgentChip
                  tone="amber"
                  label={`${delayedEventsCount} delayed events → Assignments`}
                  onClick={() => {
                    setSelectedEventTab("delayed");
                    setSelectedFleetTab("assignments");
                  }}
                />
                <UrgentChip
                  tone="blue"
                  label={`${licenceExpiryCount} licence expiry (≤30d) → Alerts`}
                  onClick={() => setSelectedFleetTab("alerts")}
                />
                <UrgentChip
                  tone="green"
                  label={`${requestsAwaitingAllocationCount} requests awaiting allocation → Fleet Requests`}
                  onClick={() => setSelectedFleetTab("requests")}
                />
              </Box>
            </Paper>
          </Box>

          {/* 4 & 5. Fleet Requests preview + Body Transfers preview */}
          <Box
            display="grid"
            gridTemplateColumns={{ xs: "1fr", md: "1.1fr 1fr" }}
            gap={1.5}
            mt={1.5}
          >
            <Paper sx={commandPanelSx} elevation={0}>
              <Box display="flex" alignItems="center" justifyContent="space-between" gap={1} mb={0.5}>
                <OperationsSectionHeader title="Fleet Requests" />
                <StandardCountBadge count={funeralFleetRequests.length} />
              </Box>
              <Stack spacing={0} divider={<Divider sx={{ borderColor: ifasColors.rule }} />}>
                {funeralFleetRequests.length === 0 ? (
                  <Typography fontSize={12.5} color={ifasColors.muted} py={1}>
                    No fleet requests recorded yet.
                  </Typography>
                ) : (
                  funeralFleetRequests.slice(0, 2).map((request: any) => (
                    <Box
                      key={request.id || request.referenceNumber}
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      gap={1}
                      py={1}
                    >
                      <Box minWidth={0}>
                        <Typography fontSize={12.5} fontWeight={900} color={ifasColors.text} noWrap>
                          {request.referenceNumber || request.planNumber || "Request"}
                        </Typography>
                        <Typography fontSize={11} fontWeight={600} color={ifasColors.muted} noWrap>
                          {request.caseNumber || "-"}
                        </Typography>
                      </Box>
                      <StatusDotChip
                        status={isRequestAwaitingAllocation(request) ? "Awaiting" : "In progress"}
                        tone={isRequestAwaitingAllocation(request) ? "amber" : "blue"}
                        compact
                      />
                    </Box>
                  ))
                )}
              </Stack>
              <Box pt={1} mt={0.25} borderTop={`1px solid ${ifasColors.border}`}>
                <Button
                  size="small"
                  onClick={() => setSelectedFleetTab("requests")}
                  sx={standardGhostBtnSx}
                >
                  View all →
                </Button>
              </Box>
            </Paper>

            <Paper sx={commandPanelSx} elevation={0}>
              <Box display="flex" alignItems="center" justifyContent="space-between" gap={1} mb={0.5}>
                <OperationsSectionHeader title="Body Transfers" />
                <StandardCountBadge count={bodyTransferTotal} />
              </Box>
              <Stack spacing={0} divider={<Divider sx={{ borderColor: ifasColors.rule }} />}>
                {bodyTransfers.length === 0 ? (
                  <Typography fontSize={12.5} color={ifasColors.muted} py={1}>
                    No branch transfers recorded yet.
                  </Typography>
                ) : (
                  bodyTransfers.slice(0, 2).map((transfer: any) => (
                    <Box
                      key={transfer.id || transfer.transferNumber}
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      gap={1}
                      py={1}
                    >
                      <Box minWidth={0}>
                        <Typography fontSize={12.5} fontWeight={900} color={ifasColors.text} noWrap>
                          {transfer.transferNumber || transfer.bodyRemovalNumber || "Transfer"}
                        </Typography>
                        <Typography fontSize={11} fontWeight={600} color={ifasColors.muted} noWrap>
                          {transfer.caseNumber || "-"}
                        </Typography>
                      </Box>
                      <StatusDotChip
                        status={formatOperationsStatus(transfer.status)}
                        tone={bodyTransferStatusTone(transfer.status)}
                        compact
                      />
                    </Box>
                  ))
                )}
              </Stack>
              <Box pt={1} mt={0.25} borderTop={`1px solid ${ifasColors.border}`}>
                <Button
                  size="small"
                  onClick={() => setSelectedFleetTab("transfers")}
                  sx={standardGhostBtnSx}
                >
                  View all →
                </Button>
              </Box>
            </Paper>
          </Box>

          <Typography fontSize={12} fontWeight={600} color={ifasColors.muted} mt={1.5} lineHeight={1.6}>
            This is a <b>summary</b> screen only. Full filterable tables, status tiles, Route Watch and
            the roster live on their own destinations — Overview links through rather than duplicating them.
          </Typography>
          </>
          )}

          {selectedFleetTab === "transfers" && (
          <>
          <Paper sx={fleetCardSx} elevation={0}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5, flexWrap: "wrap", px: 2, py: 1.4, borderBottom: `1px solid ${ifasColors.border}` }}>
              <Stack direction="row" spacing={1.1} alignItems="center">
                <Box sx={{ width: 6, height: 18, borderRadius: "999px", bgcolor: ifasColors.orange }} />
                <Typography fontSize={15} fontWeight={800} color={ifasColors.text}>Body Transfers</Typography>
                <StandardCountBadge count={bodyTransferTotal} />
              </Stack>
              <Typography fontSize={12} fontWeight={700} color={ifasColors.muted}>Inter-branch · newest first</Typography>
            </Box>

            {bodyTransfers.length === 0 ? (
              <OperationsEmptyState
                title="No branch transfers recorded yet"
                description="Body transfers appear here once a branch-to-branch transfer is created."
              />
            ) : (
              <>
              <Box sx={{ overflowX: "auto" }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={standardTableHeadCellSx}>Transfer</TableCell>
                      <TableCell sx={standardTableHeadCellSx}>Route</TableCell>
                      <TableCell sx={standardTableHeadCellSx}>Vehicle</TableCell>
                      <TableCell sx={standardTableHeadCellSx}>Driver</TableCell>
                      <TableCell sx={standardTableHeadCellSx}>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {bodyTransfers.map((transfer: any) => {
                      const statusKey = String(transfer.status || "").toUpperCase();
                      const hasReleaseGap = BODY_TRANSFER_RELEASE_GAP_STATUSES.has(statusKey) && !!transfer.vehicleNumber;
                      return (
                        <TableRow key={transfer.id || transfer.transferNumber} sx={standardTableRowSx}>
                          <TableCell>
                            <Typography fontSize={13} fontWeight={900} color={ifasColors.text}>
                              {transfer.transferNumber || transfer.bodyRemovalNumber || "Transfer"}
                            </Typography>
                            <Typography fontSize={11} fontWeight={600} color={ifasColors.muted} mt={0.15}>
                              {transfer.caseNumber || "-"}
                            </Typography>
                            {hasReleaseGap && (
                              <>
                                <Typography fontSize={10.5} fontWeight={800} color={STANDARD_TONES.amber.color} mt={0.4}>
                                  Vehicle still allocated (release-gap defect)
                                </Typography>
                                <Button
                                  size="small"
                                  onClick={() => onVehiclesClicked?.()}
                                  sx={{ ...standardManageLinkSx, mt: 0.4, minHeight: 26, fontSize: "11px", py: 0.3, px: 1 }}
                                >
                                  View in Vehicles →
                                </Button>
                              </>
                            )}
                          </TableCell>
                          <TableCell>
                            <Stack direction="row" spacing={0.7} alignItems="center">
                              <RouteOutlinedIcon sx={{ fontSize: 14, color: ifasColors.muted, flexShrink: 0 }} />
                              <Typography fontSize={12.5} fontWeight={700} color={ifasColors.text}>
                                {transfer.originBranch || "-"} → {transfer.destinationBranch || "-"}
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell>
                            <Typography fontSize={13} fontWeight={700} color={ifasColors.text}>
                              {transfer.vehicleNumber || <Box component="span" color={ifasColors.muted} fontWeight={700}>—</Box>}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography fontSize={13} fontWeight={700} color={ifasColors.text}>
                              {transfer.driverName || <Box component="span" color={ifasColors.muted} fontWeight={700}>—</Box>}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <StatusDotChip status={formatOperationsStatus(transfer.status)} tone={bodyTransferStatusTone(transfer.status)} compact />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Box>
              <StandardPager
                page={bodyTransferPage}
                total={bodyTransferTotal}
                pageSize={BODY_TRANSFER_PAGE_SIZE}
                onPage={(p) => setBodyTransferPage(p)}
              />
              </>
            )}
          </Paper>

          <Typography fontSize={11} fontWeight={800} color={ifasColors.muted} letterSpacing="0.6px" textTransform="uppercase" mt={1.75} mb={0.75}>
            Body-transfer status vocabulary — all 10 states toned
          </Typography>
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
            {(Object.keys(BODY_TRANSFER_STATUS_TONES) as Array<keyof typeof BODY_TRANSFER_STATUS_TONES>).map((status) => (
              <StatusDotChip key={status} status={formatOperationsStatus(status)} tone={BODY_TRANSFER_STATUS_TONES[status]} compact />
            ))}
          </Stack>

          <Typography fontSize={12} fontWeight={600} color={ifasColors.muted} mt={1.25} lineHeight={1.6}>
            <Box component="span" fontWeight={800} color={ifasColors.deepOrange}>One status vocabulary</Box> across vehicles,
            drivers and transfers. Every one of the 10 body-transfer states carries an explicit tone (legend above): amber
            Requested / Pending Approval / Delayed · blue Dispatched / In Transit · green Approved / Arrived / Completed
            (Arrived &amp; Completed release the vehicle) · red Rejected / Cancelled — so a stuck transfer never greys out as
            "unknown".
          </Typography>
          <Typography fontSize={12} fontWeight={600} color={ifasColors.muted} mt={0.75} lineHeight={1.6}>
            <Box component="span" fontWeight={800} color={ifasColors.deepOrange}>Open backend defects, represented not hidden:</Box> the
            body-transfer release-gap (FLEET-BUG-04) — a Rejected/Cancelled/Delayed transfer never frees its vehicle/driver (flagged
            above where it occurs) — and the unbounded <code>/fleet/portal</code> aggregate (FLEET-BUG-03) remain open. The fake
            fuel/battery/maintenance-due alert chips (FLEET-BUG-01) and the duplicate off-duty metric (FLEET-BUG-02) are no longer
            displayed here. The 30s full-screen loader, the missing error state, and the <code>onFleetClicked</code> access guard have
            been addressed in this implementation.
          </Typography>
          </>
          )}

          {selectedFleetTab === "requests" && (
          <>
          <Paper elevation={0} sx={requestsBannerSx}>
            <Stack direction="row" spacing={1.25} alignItems="center" minWidth={0}>
              <Box sx={requestsBannerIconSx}>
                <CheckCircleOutlineOutlinedIcon sx={{ fontSize: 18 }} />
              </Box>
              <Box minWidth={0}>
                <Typography fontSize={15} fontWeight={900} color={ifasColors.text}>Requests Queue</Typography>
                <Typography fontSize={12} fontWeight={500} color={ifasColors.muted} mt={0.15}>
                  Search and filter, then assign. Awaiting jobs need a driver + vehicle.
                </Typography>
              </Box>
            </Stack>
            <Stack direction="row" spacing={0.8} alignItems="center" flexWrap="wrap" useFlexGap>
              <TextField
                size="small"
                value={fleetRequestSearch}
                onChange={(event) => setFleetRequestSearch(event.target.value)}
                placeholder="Search reference, case, location…"
                sx={{
                  width: { xs: "100%", sm: 220 },
                  bgcolor: ifasColors.surface,
                  "& .MuiOutlinedInput-root": { height: 34, borderRadius: 1.5, fontSize: 11.5 },
                }}
              />
              <TextField
                select
                size="small"
                value={fleetRequestTypeFilter}
                onChange={(event) => setFleetRequestTypeFilter(event.target.value as FleetRequestTypeFilter)}
                sx={{
                  minWidth: 150,
                  bgcolor: ifasColors.surface,
                  "& .MuiOutlinedInput-root": { height: 34, borderRadius: 1.5, fontSize: 11.5 },
                }}
              >
                <MenuItem value="ALL">All types</MenuItem>
                <MenuItem value="FLEET">Funeral service</MenuItem>
                <MenuItem value="WAREHOUSE_TRANSPORT">Warehouse transport</MenuItem>
              </TextField>
              <TextField
                select
                size="small"
                value={fleetRequestStatusFilter}
                onChange={(event) => setFleetRequestStatusFilter(event.target.value as FleetRequestStatusFilter)}
                sx={{
                  minWidth: 140,
                  bgcolor: ifasColors.surface,
                  "& .MuiOutlinedInput-root": { height: 34, borderRadius: 1.5, fontSize: 11.5 },
                }}
              >
                <MenuItem value="ALL">Any status</MenuItem>
                <MenuItem value="REQUESTED">Awaiting</MenuItem>
                <MenuItem value="IN_PROGRESS">In progress</MenuItem>
              </TextField>
            </Stack>
          </Paper>

          <Paper sx={{ ...fleetCardSx, mt: 1.5 }} elevation={0}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5, flexWrap: "wrap", px: 2, py: 1.4, borderBottom: `1px solid ${ifasColors.border}` }}>
              <Stack direction="row" spacing={1.1} alignItems="center">
                <Box sx={{ width: 6, height: 18, borderRadius: "999px", bgcolor: ifasColors.orange }} />
                <Typography fontSize={15} fontWeight={800} color={ifasColors.text}>Requests</Typography>
                <StandardCountBadge count={funeralFleetRequests.length} />
              </Stack>
              <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
                <Typography fontSize={11.5} fontWeight={700} color={ifasColors.muted}>
                  <Box component="span" fontWeight={900} color={ifasColors.text}>
                    {funeralFleetRequests.filter((request: any) => request.taskType === "FLEET").length}
                  </Box> Funeral
                </Typography>
                <Typography fontSize={11.5} fontWeight={700} color={ifasColors.muted}>
                  <Box component="span" fontWeight={900} color={ifasColors.text}>
                    {funeralFleetRequests.filter((request: any) => request.taskType === "WAREHOUSE_TRANSPORT").length}
                  </Box> Warehouse
                </Typography>
                <Typography fontSize={11.5} fontWeight={700} color={STANDARD_TONES.amber.color}>
                  <Box component="span" fontWeight={900}>
                    {funeralFleetRequests.filter((request: any) => isRequestAwaitingAllocation(request)).length}
                  </Box> Awaiting
                </Typography>
              </Stack>
            </Box>

            {!funeralFleetRequests.length ? (
              <OperationsEmptyState
                title="No operational fleet requests"
                description="Funeral requests appear after confirmation; warehouse transport appears after the material checklist is created."
              />
            ) : !filteredFleetRequests.length ? (
              <OperationsEmptyState
                title="No requests match these filters"
                description="Change the request type, status, or search text to see other fleet jobs."
              />
            ) : (
              <>
              <Box sx={{ overflowX: "auto" }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={standardTableHeadCellSx}>Reference</TableCell>
                      <TableCell sx={standardTableHeadCellSx}>Case</TableCell>
                      <TableCell sx={standardTableHeadCellSx}>Type</TableCell>
                      <TableCell sx={standardTableHeadCellSx}>Journey</TableCell>
                      <TableCell sx={standardTableHeadCellSx}>Status</TableCell>
                      <TableCell sx={{ ...standardTableHeadCellSx, textAlign: "right" }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {visibleFleetRequests.map((request: any) => {
                      const isWarehouseTransport = request.taskType === "WAREHOUSE_TRANSPORT";
                      const fleetAllocations = Array.isArray(request.fleetAllocations) ? request.fleetAllocations : [];
                      const requestedSlots = isWarehouseTransport
                        ? Array.from({ length: Math.max(1, Number(request.requiredVehicleCount || 1)) }, (_, index) => ({
                            role: "WAREHOUSE_CARGO",
                            sequence: index + 1,
                            label: `Cargo Vehicle ${index + 1}`,
                          }))
                        : [
                            ...Array.from({ length: Math.max(1, Number(request.hearseVehicleCount || 1)) }, (_, index) => ({ role: "HEARSE", sequence: index + 1, label: `Hearse ${index + 1}` })),
                            ...Array.from({ length: Math.max(0, Number(request.familyVehicleCount ?? (request.familyCarRequired ? 1 : 0))) }, (_, index) => ({ role: "FAMILY_CAR", sequence: index + 1, label: `Family Car ${index + 1}` })),
                            ...Array.from({ length: Math.max(0, Number(request.additionalVehicleCount || 0)) }, (_, index) => ({ role: "ADDITIONAL_VEHICLE", sequence: index + 1, label: `Additional Vehicle ${index + 1}` })),
                          ];
                      const slotRows = requestedSlots.map((slot) => ({
                        ...slot,
                        allocation: fleetAllocations.find((allocation: any) => allocation.allocationRole === slot.role
                          && Number(allocation.allocationSequence || 1) === slot.sequence),
                      }));
                      const nextUnassignedSlot = slotRows.find((slot) => !slot.allocation);
                      const allocatedCount = slotRows.filter((slot) => slot.allocation).length;
                      const awaitingAllocation = isRequestAwaitingAllocation(request);
                      const requestKey = String(request.id || `${request.caseNumber}-${request.referenceNumber}`);
                      const expanded = expandedFleetRequests.has(requestKey);
                      const openedFromNotification = String(request.referenceNumber || "") === notificationFleetReference
                        || String(request.caseNumber || "") === notificationCaseNumber;
                      const requirementDetails = String(request.additionalRequirements || "").trim();
                      const pickup = request.pickupLocation || request.bodyLocationName || "Pickup to be confirmed";
                      const destination = request.funeralVenue || request.cemeteryDetails || "Destination to be confirmed";
                      const journeyIsPlaceholder = !request.pickupLocation && !request.bodyLocationName
                        && !request.funeralVenue && !request.cemeteryDetails;
                      const mainRef = request.referenceNumber || request.planNumber || "-";
                      const subRef = request.planNumber && request.planNumber !== mainRef
                        ? `${isWarehouseTransport ? "Prep" : "Plan"} ${request.planNumber}`
                        : undefined;
                      const rowHighlightSx = openedFromNotification
                        ? { bgcolor: `${ifasColors.softOrange} !important`, "& .MuiTableCell-root": { borderColor: ifasColors.orange } }
                        : {};
                      return (
                        <Fragment key={requestKey}>
                          <TableRow sx={{ ...standardTableRowSx, ...rowHighlightSx }}>
                            <TableCell>
                              <Typography fontSize={13} fontWeight={900} color={ifasColors.text}>{mainRef}</Typography>
                              {subRef && (
                                <Typography fontSize={11} fontWeight={600} color={ifasColors.muted} mt={0.15}>{subRef}</Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              <Typography fontSize={13} fontWeight={700} color={ifasColors.text}>{request.caseNumber || "-"}</Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                icon={isWarehouseTransport ? <LocalShippingOutlinedIcon /> : <DirectionsCarFilledOutlinedIcon />}
                                label={isWarehouseTransport ? "Warehouse" : "Funeral"}
                                size="small"
                                sx={{
                                  color: STANDARD_TONES.grey.color,
                                  bgcolor: STANDARD_TONES.grey.bg,
                                  fontWeight: 800,
                                  fontSize: 12,
                                  borderRadius: "8px",
                                  "& .MuiChip-icon": { fontSize: 14, color: ifasColors.muted },
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <Stack direction="row" spacing={0.7} alignItems="center" minWidth={0}>
                                <RouteOutlinedIcon sx={{ fontSize: 14, color: ifasColors.muted, flexShrink: 0 }} />
                                <Typography
                                  fontSize={12.5}
                                  fontWeight={700}
                                  color={journeyIsPlaceholder ? ifasColors.muted : ifasColors.text}
                                  fontStyle={journeyIsPlaceholder ? "italic" : "normal"}
                                  noWrap
                                >
                                  {pickup} → {destination}
                                </Typography>
                              </Stack>
                              {!isWarehouseTransport && (
                                <Typography fontSize={11} fontWeight={700} color={request.funeralDate ? ifasColors.muted : STANDARD_TONES.amber.color} pl={2.6} mt={0.25}>
                                  {request.funeralDate ? `Funeral ${request.funeralDate}` : "Date not scheduled"}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              <StatusDotChip status={awaitingAllocation ? "Awaiting" : "In progress"} tone={awaitingAllocation ? "amber" : "blue"} compact />
                            </TableCell>
                            <TableCell align="right">
                              <Stack direction="row" spacing={0.75} justifyContent="flex-end" alignItems="center">
                                {nextUnassignedSlot ? (
                                  <>
                                    <Typography fontSize={11} fontWeight={800} color={ifasColors.muted} whiteSpace="nowrap">
                                      {allocatedCount}/{slotRows.length}
                                    </Typography>
                                    <Button
                                      size="small"
                                      variant="contained"
                                      onClick={() => onDriversClicked?.({
                                        caseNumber: request.caseNumber,
                                        assignmentPurpose: isWarehouseTransport ? "WAREHOUSE_TRANSPORT" : "FUNERAL_SERVICE",
                                        requestReference: request.referenceNumber,
                                        allocationRole: nextUnassignedSlot.role,
                                        allocationSequence: nextUnassignedSlot.sequence,
                                      })}
                                      sx={{ minHeight: 30, whiteSpace: "nowrap", px: 1.3, borderRadius: 1.5, textTransform: "none", fontSize: 11.25, fontWeight: 900, bgcolor: ifasColors.orange, boxShadow: "none", "&:hover": { bgcolor: ifasColors.deepOrange, boxShadow: "none" } }}
                                    >
                                      Assign
                                    </Button>
                                  </>
                                ) : (
                                  <Chip
                                    icon={<CheckCircleOutlineOutlinedIcon />}
                                    label={`${allocatedCount}/${slotRows.length} allocated`}
                                    size="small"
                                    sx={{ height: 25, bgcolor: STANDARD_TONES.green.bg, color: STANDARD_TONES.green.color, fontSize: 10.25, fontWeight: 850, "& .MuiChip-icon": { fontSize: 13, color: "inherit" } }}
                                  />
                                )}
                                <Button
                                  size="small"
                                  onClick={() => toggleFleetRequestDetails(requestKey)}
                                  title="Details"
                                  sx={{
                                    ...standardRowActionBtnSx,
                                    minWidth: 34,
                                    px: 0,
                                    bgcolor: expanded ? ifasColors.softOrange : standardRowActionBtnSx.bgcolor,
                                  }}
                                >
                                  <ExpandMoreIcon sx={{ fontSize: 18, transform: expanded ? "rotate(180deg)" : "none", transition: "transform 160ms" }} />
                                </Button>
                              </Stack>
                            </TableCell>
                          </TableRow>

                          {expanded && (
                            <TableRow>
                              <TableCell colSpan={6} sx={{ p: 0, borderBottom: `1px solid ${ifasColors.border}`, bgcolor: ifasColors.page }}>
                                <Box
                                  sx={{
                                    display: "grid",
                                    gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
                                    gap: 1.25,
                                    p: { xs: 1.25, md: 1.75 },
                                  }}
                                >
                                  <StandardDetailCard
                                    title="Journey"
                                    rows={[
                                      { label: "Pickup", value: pickup },
                                      { label: isWarehouseTransport ? "Delivery" : "Funeral venue", value: destination },
                                      ...(!isWarehouseTransport && request.cemeteryDetails && request.cemeteryDetails !== destination
                                        ? [{ label: "Cemetery", value: request.cemeteryDetails }]
                                        : []),
                                      ...(!isWarehouseTransport ? [{ label: "Funeral date", value: request.funeralDate || "Not scheduled" }] : []),
                                      ...(request.funeralFileNumber ? [{ label: "Funeral file", value: request.funeralFileNumber }] : []),
                                    ]}
                                  />
                                  <Box sx={{ border: `1px solid ${ifasColors.border}`, borderRadius: "10px", bgcolor: ifasColors.surface, p: "9px 11px" }}>
                                    <Box display="flex" alignItems="center" justifyContent="space-between" gap={0.75} mb={0.6}>
                                      <Typography sx={{ fontSize: "10px", fontWeight: 800, letterSpacing: "0.4px", textTransform: "uppercase", color: ifasColors.deepOrange }}>
                                        Vehicle slots
                                      </Typography>
                                      <Typography sx={{ fontSize: "10px", fontWeight: 800, color: ifasColors.muted }}>
                                        {allocatedCount} of {slotRows.length} allocated
                                      </Typography>
                                    </Box>
                                    <Box>
                                      {slotRows.map((slot, idx) => {
                                        const allocation = slot.allocation;
                                        const completed = allocation?.status === "COMPLETED";
                                        return (
                                          <Box
                                            key={`${slot.role}-${slot.sequence}`}
                                            sx={{
                                              display: "flex",
                                              alignItems: "center",
                                              justifyContent: "space-between",
                                              gap: 1,
                                              py: "5px",
                                              borderBottom: idx === slotRows.length - 1 ? "none" : `1px solid ${ifasColors.border}`,
                                            }}
                                          >
                                            <Box minWidth={0}>
                                              <Typography fontSize={12} fontWeight={800} color={ifasColors.text} noWrap>{slot.label}</Typography>
                                              <Typography fontSize={10.5} fontWeight={allocation ? 700 : 600} color={ifasColors.muted} noWrap>
                                                {allocation
                                                  ? `${allocation.driverName || "Driver"} · ${allocation.vehicleRegistration || allocation.vehicleNumber || "Vehicle"}`
                                                  : "Driver and vehicle not assigned"}
                                              </Typography>
                                            </Box>
                                            <Box flexShrink={0} display="flex" alignItems="center" gap={0.75}>
                                              {allocation?.status === "SCHEDULED" && allocation.allocationId && (
                                                <Button
                                                  size="small"
                                                  variant="outlined"
                                                  onClick={() => void openReassignment({
                                                    ...allocation,
                                                    caseNumber: request.caseNumber,
                                                    allocationStatus: allocation.status,
                                                    assignmentPurpose: isWarehouseTransport ? "WAREHOUSE_TRANSPORT" : "FUNERAL_SERVICE",
                                                  })}
                                                  sx={{ minHeight: 26, px: 1.1, borderRadius: "7px", textTransform: "none", fontSize: 10.5, fontWeight: 800, whiteSpace: "nowrap" }}
                                                >
                                                  Replace driver or vehicle
                                                </Button>
                                              )}
                                              {!allocation ? (
                                                <Button
                                                  size="small"
                                                  onClick={() => onDriversClicked?.({
                                                    caseNumber: request.caseNumber,
                                                    assignmentPurpose: isWarehouseTransport ? "WAREHOUSE_TRANSPORT" : "FUNERAL_SERVICE",
                                                    requestReference: request.referenceNumber,
                                                    allocationRole: slot.role,
                                                    allocationSequence: slot.sequence,
                                                  })}
                                                  sx={{ minWidth: 0, minHeight: 26, px: 1.1, borderRadius: "7px", textTransform: "none", fontSize: 10.5, fontWeight: 800, color: ifasColors.deepOrange, bgcolor: ifasColors.softOrange, border: `1px solid ${alpha(ifasColors.orange, 0.32)}`, "&:hover": { bgcolor: ifasColors.softOrange } }}
                                                >
                                                  Assign
                                                </Button>
                                              ) : completed ? (
                                                <StatusDotChip status="Completed" tone="green" compact />
                                              ) : isWarehouseTransport ? (
                                                <StatusDotChip status="Allocated" tone="green" compact />
                                              ) : (
                                                <Button
                                                  size="small"
                                                  variant="contained"
                                                  disabled={completingAllocationId === allocation.allocationId}
                                                  onClick={() => setPendingCompleteTrip({
                                                    allocationId: allocation.allocationId,
                                                    driverName: allocation.driverName || "the driver",
                                                    vehicleLabel: allocation.vehicleRegistration || allocation.vehicleNumber || "the vehicle",
                                                    requestReference: request.referenceNumber || request.planNumber || "this request",
                                                  })}
                                                  sx={{ minWidth: 0, minHeight: 26, px: 1.1, borderRadius: "7px", textTransform: "none", fontSize: 10.5, fontWeight: 800, color: ifasColors.surface, bgcolor: STANDARD_TONES.blue.color, boxShadow: "none", "&:hover": { bgcolor: STANDARD_TONES.blue.color, boxShadow: "none" } }}
                                                >
                                                  {completingAllocationId === allocation.allocationId ? "Completing…" : "Complete trip"}
                                                </Button>
                                              )}
                                            </Box>
                                          </Box>
                                        );
                                      })}
                                    </Box>
                                  </Box>
                                  <StandardDetailCard
                                    title={isWarehouseTransport ? "Load summary" : "Service instructions"}
                                    rows={[
                                      {
                                        label: "Notes",
                                        value: requirementDetails || (isWarehouseTransport
                                          ? "Use the linked material checklist for loading."
                                          : "No additional vehicle instructions captured."),
                                      },
                                      {
                                        label: "Purpose",
                                        value: request.requestPurpose || (isWarehouseTransport
                                          ? "Material delivery and recollection"
                                          : "Funeral-day vehicle service"),
                                      },
                                      { label: "Requested", value: formatDateTime(request.requestedAt) },
                                    ]}
                                  />
                                </Box>
                              </TableCell>
                            </TableRow>
                          )}
                        </Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </Box>
              <StandardPager
                page={fleetRequestPage - 1}
                total={filteredFleetRequests.length}
                pageSize={FLEET_REQUEST_PAGE_SIZE}
                onPage={(p) => setFleetRequestPage(p + 1)}
                captionSuffix="· latest 50 of each status"
              />
              </>
            )}
          </Paper>

          <Typography fontSize={12} fontWeight={600} color={ifasColors.muted} mt={1.5} lineHeight={1.6}>
            <Box component="span" fontWeight={800} color={ifasColors.deepOrange}>Assign</Box> is a direct action, shown while a
            request has any unfilled vehicle slot (status stays Awaiting whenever allocated &lt; required, even 2 of 3 filled). It
            hands the job to the Drivers screen pre-scoped — passing the case number, purpose (Funeral service or Warehouse
            transport) and the request reference, then the Drivers screen posts to <code>/body-removals/drivers/assignDriver</code>.
            The collapsed row shows compact allocated/required progress (e.g. 2/3) next to Assign; once every slot is filled the row
            instead shows a done chip (3/3 allocated) and no further Assign action. In the expanded Vehicle slots panel, each
            unfilled slot carries its own Assign, each filled funeral slot carries Complete trip (frees the driver + vehicle and can
            complete the parent funeral task — not reversible, gated behind a confirm dialog), and warehouse cargo slots show an
            Allocated chip only. Type is a neutral category chip (never a status colour); the coloured dotted chip is reserved for
            real status (Awaiting = amber, In progress = blue).
          </Typography>
          <Typography fontSize={12} fontWeight={600} color={ifasColors.muted} mt={0.75} lineHeight={1.6}>
            <Box component="span" fontWeight={800} color={ifasColors.deepOrange}>Known behaviour to preserve / flag:</Box> warehouse
            assign is reference-scoped (the Drivers screen's call validates the request reference and can return a "request
            mismatch" error) while funeral assign is plan-scoped (uses the case's confirmed plan, ignores the reference). The queue
            fetches the latest 50 Awaiting + 50 In-progress only (no deeper paging) and has no error state today — both stay
            tracked as defects. This is the requests-awaiting-allocation queue; the in-flight Events &amp; Assignments table is a
            different surface (Assignments tab).
          </Typography>
          </>
          )}

          {selectedFleetTab === "vehicles" && (
          <Box
            mt={0}
            display="grid"
            gridTemplateColumns={{ xs: "1fr" }}
            gap={2}
          >
            <Paper sx={commandPanelSx} elevation={0}>
              <Box display="flex" alignItems="center" justifyContent="space-between" gap={1} flexWrap="wrap" mb={1.4}>
                <Box display="flex" alignItems="center" gap={1.1}>
                  <Box sx={{ width: 6, height: 18, borderRadius: 999, bgcolor: ifasColors.orange }} />
                  <Typography fontWeight={800} fontSize={15} color={ifasColors.text}>
                    Vehicle Status
                  </Typography>
                  <StandardCountBadge count={vehiclePanel.totalVehicles || 0} />
                </Box>
                <Button size="small" onClick={() => onVehiclesClicked?.()} sx={standardManageLinkSx}>
                  Manage → Vehicles register
                </Button>
              </Box>

              <Box display="flex" justifyContent="space-between" fontSize={12} fontWeight={700} color={ifasColors.muted} mb={0.6}>
                <span>In use</span>
                <span>
                  <Typography component="b" fontWeight={900} color={ifasColors.text}>
                    {vehiclePanel.inUseVehicles || 0}
                  </Typography>{" "}
                  of {vehiclePanel.totalVehicles || 0} ·{" "}
                  {vehiclePanel.totalVehicles
                    ? Math.round(((vehiclePanel.inUseVehicles || 0) / vehiclePanel.totalVehicles) * 100)
                    : 0}
                  %
                </span>
              </Box>
              <LinearProgress
                variant="determinate"
                value={
                  vehiclePanel.totalVehicles
                    ? Math.min(100, ((vehiclePanel.inUseVehicles || 0) / vehiclePanel.totalVehicles) * 100)
                    : 0
                }
                sx={{
                  height: 7,
                  borderRadius: 999,
                  bgcolor: STANDARD_TONES.grey.bg,
                  "& .MuiLinearProgress-bar": { bgcolor: ifasColors.orange, borderRadius: 999 },
                }}
              />

              <Box
                mt={1.4}
                display="grid"
                gridTemplateColumns={{
                  xs: "repeat(3, minmax(0, 1fr))",
                  sm: "repeat(6, minmax(0, 1fr))",
                }}
                gap={1}
              >
                {vehicleMetrics.map((metric) => (
                  <StandardCompactStatusTile key={metric.label} label={metric.label} value={metric.value} tone={metric.tone} />
                ))}
              </Box>

              <Box mt={1.6}>
                <OperationsSectionHeader title="Quick Vehicle View" meta="On active work" />
                {!vehicleAssignments.length ? (
                  <OperationsEmptyState title="No active vehicle records" />
                ) : (
                  <Stack spacing={1}>
                    {vehicleAssignments.slice(0, 3).map((vehicle: any) => {
                      const tileKey = String(resolveKey(vehicle.vehicleId, vehicle.vehicleNumber, ""));

                      return (
                        <Box
                          key={tileKey}
                          sx={{
                            ...subtleTileSx,
                            px: 1.15,
                            py: 1,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 1,
                          }}
                        >
                          <Box display="flex" alignItems="center" gap={1.1} minWidth={0}>
                            <Box
                              sx={{
                                width: 28,
                                height: 28,
                                borderRadius: "8px",
                                display: "grid",
                                placeItems: "center",
                                bgcolor: ifasColors.softOrange,
                                color: ifasColors.deepOrange,
                                fontWeight: 900,
                                fontSize: 10,
                                flexShrink: 0,
                              }}
                            >
                              {getInitials(vehicle.vehicleNumber)}
                            </Box>
                            <Box minWidth={0}>
                              <Typography fontWeight={800} fontSize={13} color={ifasColors.text} noWrap textAlign="left">
                                {vehicle.vehicleNumber || "Vehicle"} · {vehicle.currentDriverName || "Driver not assigned"}
                              </Typography>
                              <Typography fontSize={11.5} color={ifasColors.muted} fontWeight={600} noWrap textAlign="left">
                                {vehicle.currentCaseNumber || vehicle.currentBodyRemovalNumber || vehicle.currentAllocationNumber || "No live reference"}
                                {vehicle.currentLocation ? ` · ${vehicle.currentLocation}` : ""}
                              </Typography>
                            </Box>
                          </Box>
                          <Box display="flex" alignItems="center" gap={0.6} flexShrink={0}>
                            <StatusDotChip status={vehicle.status} tone={operationsStatusTone(vehicle.status)} compact />
                          </Box>
                        </Box>
                      );
                    })}
                  </Stack>
                )}
                <Typography fontSize={12} color={ifasColors.muted} fontWeight={600} mt={1.2} lineHeight={1.6}>
                  Live status — manage records in the <b>Vehicles register</b>. No edit, assign or release from here.
                </Typography>
              </Box>
            </Paper>

            <Paper sx={commandPanelSx} elevation={0}>
              <OperationsSectionHeader title="Route Watch" meta="Last known location · resolved from case & branch" />
              {!locationWatch.length ? (
                <OperationsEmptyState title="No location updates yet" description="Vehicle location will appear here once active assignments start reporting movement." />
              ) : (
                <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 1.2 }}>
                  {locationWatch.map((vehicle: any) => (
                    <Box key={`watch-${resolveKey(vehicle.vehicleId, vehicle.vehicleNumber, "")}`} sx={{ ...subtleTileSx, p: 1.25, bgcolor: ifasColors.surface }}>
                      <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
                        <Typography fontWeight={900} color={ifasColors.text}>
                          {vehicle.vehicleNumber || "Vehicle"}
                        </Typography>
                        <StatusDotChip status={vehicle.status} tone={operationsStatusTone(vehicle.status)} compact />
                      </Box>
                      <Box display="flex" alignItems="center" gap={0.75} mt={1}>
                        <PlaceOutlinedIcon sx={{ fontSize: 16, color: ifasColors.muted }} />
                        <Typography fontSize={12.5} color={ifasColors.muted} textAlign="left">
                          {vehicle.currentLocation || "Location not captured"}
                        </Typography>
                      </Box>
                      <Typography fontSize={12} color={ifasColors.muted} textAlign="left" mt={0.75}>
                        {vehicle.currentBodyRemovalNumber || vehicle.currentCaseNumber || vehicle.currentAllocationNumber || "Awaiting route assignment"}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
              <Typography fontSize={12} color={ifasColors.muted} fontWeight={600} mt={1.4} lineHeight={1.6}>
                <b>Route Watch</b> is resolved location <i>names</i> from case/branch, not GPS — no map implied.
              </Typography>
            </Paper>

            <Typography fontSize={12} color={ifasColors.muted} fontWeight={600} lineHeight={1.6}>
              <b>Honest by omission:</b> the fake <b>fuel-low / battery-low</b> chips (hardcoded 0, no sensor field) and the
              duplicate <b>maintenance-due</b> chip (a re-count of the Maintenance tile) are not shown — no invented data,
              tracked as defects rather than displayed as real. <b>Removed</b> vehicles aren't counted by this panel, so the
              tiles sum to total only while none are Removed (tracked backend gap).
            </Typography>
            <Typography fontSize={12} color={ifasColors.muted} fontWeight={600} lineHeight={1.6}>
              This is a <b>monitoring</b> surface — live status only. There is no edit, assign or release action on this
              screen; those live in the Vehicles register via <b>Manage →</b> above.
            </Typography>
          </Box>
          )}

          {selectedFleetTab === "drivers" && (
          <Box
            mt={0}
            display="grid"
            gridTemplateColumns={{ xs: "1fr" }}
            gap={2}
          >
            <Paper sx={commandPanelSx} elevation={0}>
              <Box display="flex" alignItems="center" justifyContent="space-between" gap={1} flexWrap="wrap" mb={1.4}>
                <Box display="flex" alignItems="center" gap={1.1}>
                  <Box sx={{ width: 6, height: 18, borderRadius: 999, bgcolor: ifasColors.orange }} />
                  <Typography fontWeight={800} fontSize={15} color={ifasColors.text}>
                    Driver Status
                  </Typography>
                  <StandardCountBadge count={driverPanel.totalDrivers || 0} />
                </Box>
                <Button size="small" onClick={() => onDriversClicked?.()} sx={standardManageLinkSx}>
                  Manage → Drivers register
                </Button>
              </Box>

              <Box
                display="grid"
                gridTemplateColumns={{
                  xs: "repeat(3, minmax(0, 1fr))",
                  sm: "repeat(6, minmax(0, 1fr))",
                }}
                gap={1}
              >
                {driverMetrics.map((metric) => (
                  <StandardCompactStatusTile
                    key={metric.label}
                    label={metric.label}
                    value={metric.value}
                    tone={metric.tone}
                    alert={metric.alert}
                  />
                ))}
              </Box>

              <Box display="flex" gap={1} flexWrap="wrap" mt={1.2}>
                <Box sx={{ ...subtleTileSx, flex: 1, minWidth: 130, p: "7px 10px" }}>
                  <Typography sx={{ fontSize: 10, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.3px", color: ifasColors.muted }}>
                    Current shift
                  </Typography>
                  <Typography sx={{ fontSize: 13, fontWeight: 800, color: ifasColors.text, mt: 0.25 }}>
                    {driverPanel.currentShiftLabel || "Current shift"} · {driverPanel.currentShiftDrivers || 0} on
                  </Typography>
                </Box>
                <Box sx={{ ...subtleTileSx, flex: 1, minWidth: 130, p: "7px 10px" }}>
                  <Typography sx={{ fontSize: 10, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.3px", color: ifasColors.muted }}>
                    Next shift
                  </Typography>
                  <Typography sx={{ fontSize: 13, fontWeight: 800, color: ifasColors.text, mt: 0.25 }}>
                    {driverPanel.nextShiftLabel || "Next shift"} · {driverPanel.nextShiftDrivers || 0}
                  </Typography>
                </Box>
                <Box sx={{ ...subtleTileSx, flex: 1, minWidth: 130, p: "7px 10px" }}>
                  <Typography sx={{ fontSize: 10, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.3px", color: ifasColors.muted }}>
                    Dispatched
                  </Typography>
                  <Typography sx={{ fontSize: 13, fontWeight: 800, color: ifasColors.text, mt: 0.25 }}>
                    {driverPanel.dispatchedDrivers || 0} out
                  </Typography>
                </Box>
              </Box>

              <Box mt={1.6}>
                <OperationsSectionHeader title="Driver Roster" meta="" />
                {!driverAssignments.length ? (
                  <OperationsEmptyState title="No driver activity yet" />
                ) : (
                  <Stack spacing={1}>
                    {driverAssignments.slice(0, 7).map((driver: any) => {
                      const driverKey = String(resolveKey(driver.driverId, driver.driverNumber, ""));

                      return (
                        <Box
                          key={driverKey}
                          sx={{
                            ...subtleTileSx,
                            px: 1.15,
                            py: 1,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            // Responsive pass (F.9): without wrap, the right-hand
                            // block (chip + deep-link button, flexShrink 0 and
                            // nowrap) held its full width and the only thing that
                            // could give was the driver's name, which collapsed to
                            // an ellipsis on a handheld. Letting the row wrap gives
                            // the action its own line instead of eating the identity.
                            flexWrap: { xs: "wrap", md: "nowrap" },
                            gap: 1,
                          }}
                        >
                          <Box display="flex" alignItems="center" gap={1.1} minWidth={0} sx={{ flex: "1 1 190px" }}>
                            <Avatar
                              sx={{
                                width: 28,
                                height: 28,
                                borderRadius: "8px",
                                bgcolor: ifasColors.softOrange,
                                color: ifasColors.deepOrange,
                                fontWeight: 900,
                                fontSize: 10,
                              }}
                              variant="rounded"
                            >
                              {getInitials(driver.driverName)}
                            </Avatar>
                            <Box minWidth={0}>
                              <Typography fontWeight={800} fontSize={13} color={ifasColors.text} noWrap textAlign="left">
                                {driver.driverName || "Unnamed driver"}
                              </Typography>
                              <Typography fontSize={11.5} color={ifasColors.muted} fontWeight={600} noWrap textAlign="left">
                                {driver.assignedVehicleNumber || "No vehicle"} ·{" "}
                                {driver.workShift ? formatOperationsStatus(driver.workShift) : "Shift not set"}
                              </Typography>
                            </Box>
                          </Box>
                          <Box
                            display="flex"
                            alignItems="center"
                            flexShrink={0}
                            sx={{ gap: { xs: 1.5, lg: 1.2 }, ml: { xs: 0, md: "auto" } }}
                          >
                            <StatusDotChip status={driver.status} tone={operationsStatusTone(driver.status)} compact />
                            <Button
                              size="small"
                              onClick={() => onDriversClicked?.({ focusDriverNumber: driver.driverNumber })}
                              // Responsive pass (F.9): 26px is below the 44px touch
                              // minimum and this is the only per-driver deep-link
                              // into the register. Desktop (lg+) keeps the mock's
                              // compact link exactly; touch widths get a real target.
                              sx={{ ...standardManageLinkSx, minHeight: { xs: 44, lg: 26 }, fontSize: "11px", py: { xs: 0.9, lg: 0.3 }, px: { xs: 1.4, lg: 1 } }}
                            >
                              View in Drivers register →
                            </Button>
                          </Box>
                        </Box>
                      );
                    })}
                  </Stack>
                )}
                <Typography fontSize={12} color={ifasColors.muted} fontWeight={600} mt={1.2} lineHeight={1.6}>
                  Live status — manage records in the <b>Drivers register</b>. Roster rows are read-only; no edit, assign
                  or release from here.
                </Typography>
              </Box>
            </Paper>

            <Typography fontSize={12} color={ifasColors.muted} fontWeight={600} lineHeight={1.6}>
              <b>Alerts</b> on this panel are <b>licence-expiry</b> and <b>overtime</b> — the only two driver alert types
              that are real, computed values; nothing else is fabricated into this tile. <b>Terminated / Removed</b>{" "}
              drivers aren't tiled, so the 5 status tiles sum to the register total only while none are in those terminal
              states — the same tracked backend gap disclosed on Vehicles for Removed.
            </Typography>
            <Typography fontSize={12} color={ifasColors.muted} fontWeight={600} lineHeight={1.6}>
              This is a <b>monitoring</b> surface — live status only. There is no edit, assign or release action on this
              screen; those live in the Drivers register via <b>Manage →</b> above.
            </Typography>
          </Box>
          )}

          {selectedFleetTab === "assignments" && (
          <Box mt={0}>
            <Paper sx={fleetCardSx} elevation={0}>
              <Box sx={{ p: { xs: 1.5, md: 1.9 }, pb: 0.6 }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" gap={1} flexWrap="wrap">
                  <Box display="flex" alignItems="center" gap={1.1}>
                    <OperationsSectionHeader
                      title="Events & Assignments"
                      meta={`${EVENT_TABS.find((tab) => tab.key === selectedEventTab)?.label || ""} tab`}
                    />
                    <StandardCountBadge count={eventTabCounts[selectedEventTab]} />
                  </Box>
                  <Box display="flex" gap={0.8} flexWrap="wrap">
                    {EVENT_TABS.map((tab) => {
                      const active = selectedEventTab === tab.key;
                      const t = STANDARD_TONES[tab.tone];
                      const inactive = STANDARD_TONES.grey;
                      return (
                        <Button
                          key={tab.key}
                          size="small"
                          onClick={() => setSelectedEventTab(tab.key)}
                          sx={{
                            minWidth: 0,
                            px: 1.2,
                            py: 0.6,
                            borderRadius: 999,
                            textTransform: "none",
                            fontWeight: 900,
                            color: active ? t.color : inactive.color,
                            bgcolor: active ? t.bg : inactive.bg,
                            border: `1px solid ${active ? alpha(t.color, 0.22) : ifasColors.border}`,
                            "&:hover": {
                              bgcolor: active ? t.bg : inactive.bg,
                            },
                          }}
                        >
                          {tab.label} {eventTabCounts[tab.key]}
                        </Button>
                      );
                    })}
                  </Box>
                </Box>

                <Box display="flex" gap={1} flexWrap="wrap" mt={1.4}>
                  <TextField
                    size="small"
                    value={eventSearch}
                    onChange={(event) => setEventSearch(event.target.value)}
                    placeholder="Registration, driver, case, route…"
                    sx={{
                      width: { xs: "100%", sm: 240 },
                      bgcolor: ifasColors.surface,
                      "& .MuiOutlinedInput-root": { height: 36, borderRadius: 1.5, fontSize: 12.5 },
                    }}
                  />
                  <TextField
                    select
                    size="small"
                    value={eventVehicleTypeFilter}
                    onChange={(event) => setEventVehicleTypeFilter(event.target.value)}
                    sx={{
                      minWidth: 160,
                      bgcolor: ifasColors.surface,
                      "& .MuiOutlinedInput-root": { height: 36, borderRadius: 1.5, fontSize: 12.5 },
                    }}
                  >
                    <MenuItem value="ALL">All vehicle types</MenuItem>
                    {eventVehicleTypeOptions.map((type) => (
                      <MenuItem key={type} value={type}>{formatOperationsStatus(type)}</MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    select
                    size="small"
                    value={eventStatusFilter}
                    onChange={(event) => setEventStatusFilter(event.target.value as EventStatusFilter)}
                    sx={{
                      minWidth: 170,
                      bgcolor: ifasColors.surface,
                      "& .MuiOutlinedInput-root": { height: 36, borderRadius: 1.5, fontSize: 12.5 },
                    }}
                  >
                    <MenuItem value="ALL">All statuses</MenuItem>
                    <MenuItem value="ON_TIME">On time</MenuItem>
                    <MenuItem value="DELAYED">Delayed / At Risk</MenuItem>
                    <MenuItem value="COMPLETED">Completed</MenuItem>
                    <MenuItem value="CANCELLED">Cancelled</MenuItem>
                  </TextField>
                </Box>
              </Box>

              {!eventRows.length ? (
                <OperationsEmptyState
                  title="No events in this queue"
                  description="Switch tabs to inspect another part of the operating day."
                />
              ) : !filteredEventRows.length ? (
                <OperationsEmptyState
                  title="No events match these filters"
                  description="Change the search text, vehicle type or status to see other assignments."
                />
              ) : (
                <>
                <Box sx={{ overflowX: "auto" }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={standardTableHeadCellSx}>Vehicle / Driver</TableCell>
                      <TableCell sx={standardTableHeadCellSx}>Case / Route</TableCell>
                      <TableCell sx={standardTableHeadCellSx}>Sched.</TableCell>
                      <TableCell sx={standardTableHeadCellSx}>Stage</TableCell>
                      <TableCell sx={standardTableHeadCellSx}>Event</TableCell>
                      <TableCell sx={{ ...standardTableHeadCellSx, textAlign: "right" }}>Open</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {visibleEventRows.map((item: any) => {
                      const rowKey = String(resolveKey(item.allocationId, item.allocationNumber, ""));
                      const isOpen = rowKey === String(selectedEventKey) && rowKey !== "";
                      const timeliness = eventTimelinessTone(item.statusIndicator || item.operationalStatus);
                      const assignedVehicle = vehicleByNumber.get(String(item.vehicleNumber || ""));

                      return (
                        <Fragment key={rowKey || item.allocationNumber}>
                          <TableRow
                            sx={{
                              ...standardTableRowSx,
                              bgcolor: isOpen ? ifasColors.softOrange : "transparent",
                              "& .MuiTableCell-root:first-of-type": {
                                boxShadow: isOpen ? `inset 3px 0 0 ${ifasColors.orange}` : undefined,
                              },
                            }}
                          >
                            <TableCell>
                              <Typography fontWeight={900} fontSize={13} color={ifasColors.text}>
                                {item.vehicleNumber || "Unassigned"}
                                {item.vehicleType ? ` · ${formatOperationsStatus(item.vehicleType)}` : ""}
                              </Typography>
                              <Typography fontSize={11.5} color={ifasColors.muted} fontWeight={600} mt={0.25}>
                                {item.driverName || "Driver not assigned"}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography fontWeight={900} fontSize={13} color={ifasColors.text}>
                                {item.caseNumber || item.bodyRemovalNumber || "No case"}
                              </Typography>
                              <Typography fontSize={11.5} color={ifasColors.muted} fontWeight={600} mt={0.25}>
                                {item.pickupLocation || "Pickup not set"} → {item.destinationLocation || "Destination not set"}
                                {item.allocationNumber ? ` · ${item.allocationNumber}` : ""}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography fontSize={12.5} fontWeight={700} color={ifasColors.text}>
                                {item.scheduledDate || "Not scheduled"}
                              </Typography>
                              <Typography fontSize={11.5} color={ifasColors.muted} fontWeight={600}>
                                {item.scheduledTime || ""}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <StatusDotChip status={formatOperationsStatus(item.operationalStatus)} tone={operationsStatusTone(item.operationalStatus)} compact />
                            </TableCell>
                            <TableCell>
                              <StatusDotChip status={timeliness.label} tone={timeliness.tone} compact />
                            </TableCell>
                            <TableCell align="right">
                              <Button
                                size="small"
                                onClick={() => setSelectedEventKey(isOpen ? "" : rowKey)}
                                endIcon={<ExpandMoreIcon sx={{ fontSize: 16, transform: isOpen ? "rotate(180deg)" : "none", transition: "transform 160ms" }} />}
                                title={isOpen ? "Collapse assignment detail" : "Open assignment detail"}
                                sx={{
                                  ...standardRowActionBtnSx,
                                  border: `1px solid ${isOpen ? alpha(ifasColors.orange, 0.4) : ifasColors.border}`,
                                  color: isOpen ? ifasColors.deepOrange : standardRowActionBtnSx.color,
                                  bgcolor: isOpen ? ifasColors.softOrange : standardRowActionBtnSx.bgcolor,
                                }}
                              >
                                Open
                              </Button>
                            </TableCell>
                          </TableRow>

                          {isOpen && (
                            <TableRow>
                              <TableCell colSpan={6} sx={{ p: 0, borderBottom: `1px solid ${ifasColors.border}`, bgcolor: ifasColors.page }}>
                                <Box sx={{ p: { xs: 1.25, md: 1.75 } }}>
                                  <Box display="flex" alignItems="center" justifyContent="space-between" gap={1} flexWrap="wrap" mb={1}>
                                    <Box display="flex" alignItems="center" gap={1}>
                                      <Typography fontWeight={900} fontSize={14} color={ifasColors.text}>
                                        {item.allocationNumber || "Allocation"}
                                      </Typography>
                                      <Typography fontSize={11.5} fontWeight={700} color={ifasColors.muted}>
                                        {item.caseNumber || item.bodyRemovalNumber || "No case"} · this row's full detail — the dissolved Operations Focus
                                      </Typography>
                                    </Box>
                                    <StatusDotChip status={timeliness.label} tone={timeliness.tone} compact />
                                  </Box>

                                  <Box
                                    display="grid"
                                    gridTemplateColumns={{ xs: "1fr", md: "repeat(3, 1fr)" }}
                                    gap={1.25}
                                  >
                                    <StandardDetailCard
                                      title="Allocation"
                                      rows={[
                                        { label: "Allocation number", value: item.allocationNumber || "N/A" },
                                        { label: "Case number", value: item.caseNumber || item.bodyRemovalNumber || "No case" },
                                        { label: "Schedule", value: formatSchedule(item.scheduledDate, item.scheduledTime) },
                                      ]}
                                    />
                                    <StandardDetailCard
                                      title="Route"
                                      rows={[
                                        { label: "Pickup", value: item.pickupLocation || "Pickup not set" },
                                        { label: "Destination", value: item.destinationLocation || "Destination not set" },
                                        { label: "Timeliness", value: <StatusDotChip status={timeliness.label} tone={timeliness.tone} compact /> },
                                      ]}
                                    />
                                    <StandardDetailCard
                                      title="Driver & vehicle"
                                      rows={[
                                        { label: "Driver", value: item.driverName || "Driver not assigned" },
                                        { label: "Vehicle", value: item.vehicleNumber || "Vehicle not assigned" },
                                        {
                                          label: "Stage",
                                          value: <StatusDotChip status={formatOperationsStatus(item.operationalStatus)} tone={operationsStatusTone(item.operationalStatus)} compact />,
                                        },
                                      ]}
                                    />
                                  </Box>

                                  <Box
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="space-between"
                                    gap={1.25}
                                    flexWrap="wrap"
                                    mt={1.25}
                                    sx={{ p: "9px 12px", border: `1px dashed ${ifasColors.border}`, borderRadius: "10px", bgcolor: ifasColors.surface }}
                                  >
                                    <Box display="flex" alignItems="center" gap={0.9}>
                                      <PlaceOutlinedIcon sx={{ fontSize: 16, color: ifasColors.muted }} />
                                      <Typography fontSize={12.5} fontWeight={700} color={ifasColors.text}>
                                        {item.vehicleNumber || "Vehicle"} currently at{" "}
                                        <Box component="span" fontWeight={900}>
                                          {assignedVehicle?.currentLocation || "Location not captured"}
                                        </Box>
                                      </Typography>
                                    </Box>
                                    <Button
                                      size="small"
                                      onClick={() => onVehiclesClicked?.()}
                                      endIcon={<ArrowOutwardOutlinedIcon sx={{ fontSize: 14 }} />}
                                      sx={standardManageLinkSx}
                                    >
                                      View in Vehicles
                                    </Button>
                                  </Box>
                                  {item.allocationStatus === "SCHEDULED" && item.allocationId && (
                                    <Button variant="outlined" size="small" sx={{ mt: 1.5 }}
                                      onClick={() => void openReassignment(item)}>
                                      Replace driver or vehicle
                                    </Button>
                                  )}
                                </Box>
                              </TableCell>
                            </TableRow>
                          )}
                        </Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
                </Box>
                <StandardPager
                  page={eventPage - 1}
                  total={filteredEventRows.length}
                  pageSize={EVENT_PAGE_SIZE}
                  onPage={(p) => setEventPage(p + 1)}
                  captionSuffix={(EVENT_TABS.find((tab) => tab.key === selectedEventTab)?.label || "").toLowerCase()}
                />
                </>
              )}
            </Paper>

            <Typography fontSize={12} color={ifasColors.muted} fontWeight={600} mt={1.5} lineHeight={1.6}>
              <b>Operations Focus dissolved:</b> detail opens via an explicit <b>Open</b> row action, not a click-anywhere row —
              the row still highlights while its detail is open. Selected-Vehicle and Selected-Driver fields moved to the
              Vehicles and Drivers tabs' own quick views; nothing shown here duplicates them beyond the single read-only
              Route-Watch line and its <b>View in Vehicles →</b> cross-link.
            </Typography>
          </Box>
          )}

          {selectedFleetTab === "alerts" && (
          <>
          <Box
            mt={0}
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 1.5,
              "@media (max-width:820px)": { gridTemplateColumns: "1fr" },
            }}
          >
            <FleetAlertPanel
              title="Driver Alerts"
              items={driverAlerts}
              linkLabel="View driver"
              onOpen={() => setSelectedFleetTab("drivers")}
            />
            <FleetAlertPanel
              title="Vehicle Alerts"
              items={vehicleAlerts}
              linkLabel="View vehicle"
              onOpen={() => setSelectedFleetTab("vehicles")}
            />
          </Box>

          <Box
            sx={{
              display: "flex",
              gap: 1.1,
              alignItems: "flex-start",
              mt: 1.5,
              p: 1.4,
              borderRadius: 2,
              bgcolor: STANDARD_TONES.grey.bg,
              color: ifasColors.text,
            }}
          >
            <WarningAmberOutlinedIcon sx={{ fontSize: 18, color: ifasColors.muted, mt: 0.1, flexShrink: 0 }} />
            <Typography fontSize={11.5} fontWeight={600} lineHeight={1.6}>
              <b>FLEET-BUG-01 / FLEET-BUG-02 — tracked defects, deliberately excluded:</b> the fake{" "}
              <b>fuel-low</b> and <b>battery-low</b> chips (hardcoded to 0, no sensor field backs them) and
              the duplicate <b>maintenance-due</b> chip (a re-count of the Vehicle Status Maintenance tile)
              are <b>not</b> presented as alerts here. Nor is the driver "Off Duty" count, which is really a
              duplicate of the <b>Unavailable</b> tile already shown once on Vehicles/Drivers. These stay
              open defects to resolve against genuine data — not silently fixed by omission, and not faked
              by inclusion.
            </Typography>
          </Box>

          <Typography fontSize={12} fontWeight={600} color={ifasColors.muted} mt={1.25} lineHeight={1.6}>
            <b>Only genuine, computed alerts appear here</b> — licence-expiry (30-day window), overtime
            (10h threshold), trip-delay (2h threshold) and maintenance-from-status. That is {driverAlerts.length}{" "}
            Driver Alert{driverAlerts.length === 1 ? "" : "s"} + {vehicleAlerts.length} Vehicle Alert
            {vehicleAlerts.length === 1 ? "" : "s"} = <b>{fleetAlertCount} total</b>, matching the global
            strip's alert count and the Alerts tab badge above. Each row links through to its driver or
            vehicle record for full context and action — Alerts itself is read-only.
          </Typography>
          </>
          )}
        </>
      )}
      <OperationsConfirmDialog
        open={Boolean(pendingCompleteTrip)}
        title="Complete this trip?"
        message={pendingCompleteTrip
          ? `This frees ${pendingCompleteTrip.driverName} and ${pendingCompleteTrip.vehicleLabel} back to available, and may complete the funeral task for ${pendingCompleteTrip.requestReference} if this was the last open slot. This action can't be undone.`
          : ""}
        confirmLabel="Complete trip"
        busy={Boolean(pendingCompleteTrip && completingAllocationId === pendingCompleteTrip.allocationId)}
        onConfirm={() => pendingCompleteTrip && completeFleetTrip(pendingCompleteTrip.allocationId)}
        onCancel={() => setPendingCompleteTrip(null)}
      />
      <Dialog
        open={Boolean(reassignment)}
        onClose={() => !savingReassignment && setReassignment(null)}
        fullWidth
        maxWidth="sm"
        aria-labelledby="fleet-reassignment-title"
        slotProps={{
          backdrop: { sx: { bgcolor: "rgba(15, 23, 42, 0.52)", backdropFilter: "blur(6px)" } },
          paper: {
            sx: {
              width: { xs: "calc(100% - 24px)", sm: "min(580px, calc(100% - 48px))" },
              m: { xs: 1.5, sm: 3 },
              maxHeight: "min(720px, calc(100vh - 32px))",
              borderRadius: 3,
              overflow: "hidden",
              border: `1px solid ${ifasColors.border}`,
              boxShadow: "0 28px 70px rgba(15, 23, 42, 0.3)",
            },
          },
        }}
      >
        <DialogTitle
          id="fleet-reassignment-title"
          sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, px: { xs: 2, sm: 3 }, py: 2.25,
            borderBottom: `1px solid ${ifasColors.border}`,
            background: `linear-gradient(135deg, ${ifasColors.softOrange} 0%, ${ifasColors.surface} 75%)` }}
        >
          <Avatar sx={{ width: 44, height: 44, bgcolor: ifasColors.surface, color: ifasColors.deepOrange,
            border: `1px solid ${alpha(ifasColors.orange, 0.28)}`, flexShrink: 0 }}>
            <BadgeOutlinedIcon />
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography component="h2" fontSize={{ xs: 17, sm: 19 }} fontWeight={900} color={ifasColors.text} lineHeight={1.25}>
              Replace driver or vehicle
            </Typography>
            <Typography fontSize={12.5} color={ifasColors.muted} mt={0.45} lineHeight={1.45}>
              Update the resources assigned to this scheduled duty.
            </Typography>
          </Box>
          <IconButton aria-label="Close replacement dialog" size="small"
            onClick={() => setReassignment(null)} disabled={savingReassignment}
            sx={{ color: ifasColors.muted, mt: -0.4, mr: -0.5, "&:hover": { bgcolor: ifasColors.surface } }}>
            <CloseOutlinedIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2.25,
          px: { xs: 2, sm: 3 }, py: "24px !important", bgcolor: ifasColors.surface }}>
          <Box sx={{ border: `1px solid ${ifasColors.border}`, borderRadius: 2, bgcolor: ifasColors.page, p: 1.5 }}>
            <Typography fontSize={10.5} fontWeight={900} letterSpacing={0.5} color={ifasColors.deepOrange} textTransform="uppercase">
              Current assignment
            </Typography>
            <Typography fontSize={13} fontWeight={800} color={ifasColors.text} mt={0.5} sx={{ overflowWrap: "anywhere" }}>
              {reassignment?.allocationNumber || "Allocation"} · {reassignment?.caseNumber || "Case"}
            </Typography>
            <Typography fontSize={12} color={ifasColors.muted} mt={0.5}>
              {reassignment?.driverName || "Current driver"} · {reassignment?.vehicleNumber || "Current vehicle"}
            </Typography>
          </Box>
          <Box>
            <Typography fontSize={12} fontWeight={800} color={ifasColors.text} mb={0.85}>Driver</Typography>
            <TextField select value={replacementDriverId} size="small" fullWidth
              aria-label="Replacement driver"
              onChange={(event) => {
                const driverId = event.target.value;
                const driver = driverId === reassignment?.driverId
                  ? { licenseCode: reassignment?.driverLicenseCode, hasPdp: reassignment?.driverHasPdp }
                  : availableDrivers.find((item: any) => item.driverID === driverId);
                const vehicle = replacementVehicleId === reassignment?.vehicleId
                  ? currentReassignmentVehicle
                  : availableVehicles.find((item: any) => item.vehicleID === replacementVehicleId);
                setReplacementDriverId(driverId);
                if (driver && vehicle && !canDriverUseVehicle(driver, vehicle)) setReplacementVehicleId("");
              }}
              SelectProps={{ MenuProps: { PaperProps: { sx: { maxHeight: 260, "& .MuiMenuItem-root": { fontSize: 13, py: 1 } } } } }}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}>
              <MenuItem value={reassignment?.driverId || ""}>
                {reassignment?.driverName || "Current driver"} (current)
              </MenuItem>
              {availableDrivers.filter((driver: any) => driver.driverID !== reassignment?.driverId)
                .map((driver: any) => (
                  <MenuItem key={driver.driverID} value={driver.driverID}>
                    {driver.firstName} {driver.lastName} · {driver.driverNumber}
                  </MenuItem>
                ))}
            </TextField>
          </Box>
          <Box>
            <Typography fontSize={12} fontWeight={800} color={ifasColors.text} mb={0.85}>Vehicle</Typography>
            <TextField select value={replacementVehicleId} size="small" fullWidth
              aria-label="Replacement vehicle"
              disabled={!replacementDriverId}
              onChange={(event) => setReplacementVehicleId(event.target.value)}
              SelectProps={{ MenuProps: { PaperProps: { sx: { maxHeight: 260, "& .MuiMenuItem-root": { fontSize: 13, py: 1 } } } } }}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}>
              <MenuItem value="" disabled>Choose a compatible vehicle</MenuItem>
              <MenuItem value={reassignment?.vehicleId || ""}
                disabled={!replacementDriver || !canDriverUseVehicle(replacementDriver, currentReassignmentVehicle)}>
                {reassignment?.vehicleNumber || "Current vehicle"} (current)
              </MenuItem>
              {availableVehicles.filter((vehicle: any) => vehicle.vehicleID !== reassignment?.vehicleId
                && (reassignment?.allocationRole === "ADDITIONAL_VEHICLE"
                  || vehicle.vehicleType === reassignment?.vehicleType)
                && replacementDriver && canDriverUseVehicle(replacementDriver, vehicle))
                .map((vehicle: any) => (
                  <MenuItem key={vehicle.vehicleID} value={vehicle.vehicleID}>
                    {vehicle.vehicleNumber} · {formatOperationsStatus(vehicle.vehicleType)}
                  </MenuItem>
                ))}
            </TextField>
          </Box>
          <Box>
            <Typography fontSize={12} fontWeight={800} color={ifasColors.text} mb={0.85}>Reason for replacement</Typography>
            <TextField value={reassignmentReason} onChange={(event) => setReassignmentReason(event.target.value)}
              placeholder="For example, driver unavailable due to illness" fullWidth multiline minRows={2}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: 1.75, gap: 1,
          borderTop: `1px solid ${ifasColors.border}`, bgcolor: ifasColors.page }}>
          <Button onClick={() => setReassignment(null)} disabled={savingReassignment}
            sx={{ textTransform: "none", borderRadius: 2, fontWeight: 800, color: ifasColors.text,
              bgcolor: ifasColors.surface, border: `1px solid ${ifasColors.border}`, px: 2.5 }}>
            Cancel
          </Button>
          <Button onClick={() => void saveReassignment()} disabled={savingReassignment} variant="contained"
            sx={{ textTransform: "none", borderRadius: 2, fontWeight: 800, color: ifasColors.surface,
              bgcolor: ifasColors.orange, px: 2.5, boxShadow: "none",
              "&:hover": { bgcolor: ifasColors.deepOrange, boxShadow: "none" } }}>
            {savingReassignment ? "Saving..." : "Save replacement"}
          </Button>
        </DialogActions>
      </Dialog>
    </OperationsPageShell>
  );
}
